import { isFundingDevelopmentBadge } from '../../../../shared/utils/development-badge.util';
import { propertyDevelopmentLabel, propertyDevelopmentHeading } from '../../../../shared/utils/property-construction-status.util';
import { Component, OnInit, OnDestroy, inject, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ReservationSessionService, ReservationOtpChannel } from '../../../../core/services/reservation-session.service';
import { LeadsService } from '../../../../core/services/leads.service';
import { PostSaleService } from '../../../../core/services/post-sale.service';
import { InvestorsService, InvestorSearchResult } from '../../../../core/services/investors.service';
import { PhoneInputComponent } from '../../../../shared/components/phone-input/phone-input.component';
import { Store } from '@ngrx/store';
import { selectCurrentUser, selectActor } from '../../../../store/auth/auth.selectors';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PropertiesService } from '../../../../core/services/properties.service';
import { Property, PaymentPlan, OfferRequirementItem } from '../../../../core/models/property.model';
import { CurrencyService } from '../../../../core/services/currency.service';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';
import { LucideIconsModule } from '../../../../shared/lucide-icons.module';
import { ExternalViewerDialogService } from '../../../../shared/components/external-viewer-dialog/external-viewer-dialog.service';
import { LuxuryImageViewerComponent } from '../../../../shared/components/luxury-image-viewer/luxury-image-viewer.component';
import { NotificationService } from '../../../../services/notification.service';
import { ReservationsService } from '../../../../core/services/reservations.service';
import { ReservationRecord, ReservationResult, ReservationKycRecord } from '../../../../core/models/reservation.model';
import { OffersService, OfferRequestResult } from '../../../../core/services/offers.service';
import { OfferApprovalsService } from '../../../../core/services/offer-approvals.service';
import { PropertyUploadService } from '../../../../core/services/property-upload.service';
import { ConfirmDialogService } from '../../../../shared/components/confirm-dialog/confirm-dialog.service';
import { FilesService } from '../../../../core/services/files.service';
import { firstValueFrom, Subscription, Subject } from 'rxjs';
import { take, debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { stratusProdFallbackUrl } from '../../../../shared/utils/stratus-url.util';
import { formatApiErrorMessage } from '../../../../shared/utils/api-error.util';
import { resolvePostSaleFollowUpEnabled } from '../../../../core/config/feature-flags';
import { PROPERTY_STATUS_EDIT_OPTIONS, propertyStatusEditValue, shouldShowPropertyStatusBadge } from '../../../../shared/utils/property-status.util';

type OfferFlowRecipientMode = 'self' | 'existing' | 'new';
type ReserveFlowKycMode = 'choose' | 'ai' | 'manual';

@Component({
  selector: 'app-developer-property-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, LoadingSpinnerComponent, LucideIconsModule, LuxuryImageViewerComponent, PhoneInputComponent],
  templateUrl: './developer-property-detail.component.html'
})
export class DeveloperPropertyDetailComponent implements OnInit, OnDestroy {
  private reservationSessionService = inject(ReservationSessionService);
  private leadsService = inject(LeadsService);
  private postSaleService = inject(PostSaleService);
  private investorsService = inject(InvestorsService);
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private propertiesService = inject(PropertiesService);
  private router = inject(Router);
  private viewerDialog = inject(ExternalViewerDialogService);
  private notifications = inject(NotificationService);
  private reservationsService = inject(ReservationsService);
  private offersService = inject(OffersService);
  private offerApprovalsService = inject(OfferApprovalsService);
  private uploads = inject(PropertyUploadService);
  private confirm = inject(ConfirmDialogService);
  private filesService = inject(FilesService);
  private sanitizer = inject(DomSanitizer);
  currencyService = inject(CurrencyService);

  private apiError(err: any, fallback: string): string {
    return formatApiErrorMessage(err, { fallback });
  }

  private authSub: Subscription | null = null;
  private actorSub: Subscription | null = null;
  private searchSub: Subscription | null = null;
  private viewerClosedSub: Subscription | null = null;
  private searchInput$ = new Subject<string>();

  property: Property | null = null;
  loading = true;
  error: string | null = null;
  activeIndex = 0;
  maxThumbnails = 8;
  galleryImages: Property['images'] = [];
  placeholderImage = 'assets/images/placeholders/placeholder.svg';
  imageErrors: Record<string, boolean> = {};
  imageFallbacks: Record<string, string> = {};

  deleteError: string | null = null;
  showDeleteModal = false;
  deleting = false;
  showImageModal = false;
  modalImageIndex = 0;
  modalImages: Property['images'] = [];

  reservations: ReservationRecord[] = [];
  reservationsLoading = false;
  reservationsError: string | null = null;
  selectedLeadIds = new Set<string>();
  sendingOffers = false;
  bulkSendResult: { sent: number; skipped: number; errors: Array<{ lead_id: string; code?: string | null; message?: string | null }> } | null = null;
  offerDefaultsPlanId: string | null = null;
  offerDefaultsNotes = '';
  savingOfferDefaults = false;
  offerDefaultsError: string | null = null;

  updatingStatus = false;
  currentUser: any = null;
  currentUserEmail: string | null = null;
  permissionCodes: string[] = [];
  currentUserOrgId: string | null = null;
  viewerIsAppAdmin = false;

  phases: any[] = [];
  phasesLoading = false;
  phasesError: string | null = null;

  postSaleLoading = false;
  postSaleCases: any[] = [];
  postSaleFollowUpEnabled = true;

  selectedDetailPlanId: string | null = null;

  get selectedDetailPlanSummary(): any {
    if (!this.selectedDetailPlanId || !this.property?.paymentPlans) return null;
    const plan = this.property.paymentPlans.find(p => p.id === this.selectedDetailPlanId);
    return plan ? this.paymentPlanSummary(plan, this.property) : null;
  }

  get leadPlanSelectedPlanSummary(): any {
    if (!this.leadPlanSelectedId || !this.property?.paymentPlans) return null;
    const plan = this.property.paymentPlans.find(p => p.id === this.leadPlanSelectedId);
    return plan ? this.paymentPlanSummary(plan, this.property) : null;
  }

  get mapEmbedUrl(): SafeResourceUrl | null {
    const loc = this.property?.location;
    if (!loc) {
      this.mapEmbedCache = null;
      this.mapEmbedCacheKey = null;
      return null;
    }
    // The Google Maps embed binds to <iframe [src]>, a resource-URL context.
    // Angular rejects a raw string there (NG0904 → blank map), so it must be a
    // SafeResourceUrl. Cache by location so we don't re-sanitize every CD tick.
    if (this.mapEmbedCacheKey !== loc) {
      const url = `https://maps.google.com/maps?q=${encodeURIComponent(loc)}&output=embed`;
      this.mapEmbedCache = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.mapEmbedCacheKey = loc;
    }
    return this.mapEmbedCache;
  }

  // A property shared with us via a partner grant belongs to another org. Grants
  // never carry properties:edit/write/delete (the server refuses those writes), so
  // the edit/status/delete controls must stay hidden for foreign-org inventory even
  // when the actor holds those codes as their own org's owner baseline.
  get isForeignOrgProperty(): boolean {
    const propertyOrg = this.property?.organizationId ? String(this.property.organizationId).trim() : null;
    const actorOrg = this.currentUserOrgId ? String(this.currentUserOrgId).trim() : null;
    if (!propertyOrg || !actorOrg) return false;
    return propertyOrg !== actorOrg;
  }

  get canEditStatus(): boolean {
    if (this.isForeignOrgProperty) return false;
    return this.permissionCodes.includes('properties.fields.status:edit') || this.permissionCodes.includes('properties:approve') || this.permissionCodes.includes('properties:edit') || this.permissionCodes.includes('properties:update');
  }

  get propertyStatusOptions(): { value: string; label: string }[] {
    return PROPERTY_STATUS_EDIT_OPTIONS;
  }

  get propertyStatusEditValue(): string {
    return propertyStatusEditValue(this.property?.statusBadge || this.property?.status);
  }

  get selectedLeadCount(): number { return this.selectedLeadIds.size; }

  get canEditProperty(): boolean {
    if (this.isForeignOrgProperty) return false;
    return this.permissionCodes.includes('properties:edit') || this.permissionCodes.includes('properties:update');
  }

  onEditClick(event: Event): void {
    if (!this.canEditProperty) {
      event.preventDefault();
      this.notifications.showAccessRestricted().subscribe();
    }
  }

  get canDeleteProperty(): boolean {
    if (this.isForeignOrgProperty) return false;
    return this.permissionCodes.includes('properties:delete') || this.permissionCodes.includes('properties:write');
  }

  deletingProperty = false;

  deleteProperty(): void {
    if (!this.canDeleteProperty) {
      this.notifications.showAccessRestricted().subscribe();
      return;
    }
    if (!this.property?.id || this.deletingProperty) return;
    const title = this.property.title || this.property.unitNumber || 'this property';
    if (!confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) return;
    this.deletingProperty = true;
    this.propertiesService.deleteProperty(String(this.property.id)).subscribe({
      next: () => {
        this.deletingProperty = false;
        this.router.navigate(['/developer/properties']);
      },
      error: (err: any) => {
        this.deletingProperty = false;
        const message = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to delete property';
        alert(message);
      }
    });
  }

  get canOpenReservationFlow(): boolean {
    return (
      (this.permissionCodes.includes('reservations:write') || this.permissionCodes.includes('reservations:reserve_for_client')) &&
      this.developerReservationEnabled
    );
  }

  get developerReservationEnabled(): boolean {
    return this.currentUser?.feature_flags?.fast_reservation_enabled !== false;
  }

  get offersFeatureEnabled(): boolean {
    return this.property?.feature_flags?.reservations_enabled !== false;
  }

  get zohoSignEnabled(): boolean {
    return this.currentUser?.feature_flags?.zoho_sign_enabled !== false;
  }

  get leadActionAvailabilityMessage(): string | null {
    if (!this.property?.id) return 'No property loaded';
    if (this.property.status === 'sold') return 'Unit is sold';
    return null;
  }

  // ─── Offer Flow ───────────────────────────────────────────────────────────

  offerFlowOpen = false;
  offerFlowStep = 1;
  offerFlowTotalSteps = 4;
  offerFlowSubmitting = false;
  offerFlowError: string | null = null;
  offerFlowRecipientMode: OfferFlowRecipientMode | null = null;
  offerFlowSelectedLeadId: string | null = null;
  offerFlowCreatedLeadId: string | null = null;
  offerFlowPaymentPlanId: string | null = null;
  offerEmailDisabledReason: string | null = null;
  offerReviewConfirmed = false;
  newLeadForm = { firstName: '', lastName: '', email: '', phone: { countryCode: 'EG', phone: '' } as { countryCode: string; phone: string } };

  offerReviewLoading = false;
  offerReviewError: string | null = null;
  offerReviewUrl: SafeResourceUrl | null = null;
  private mapEmbedCache: SafeResourceUrl | null = null;
  private mapEmbedCacheKey: string | null = null;

  get offerFlowSelectedPlanSummary(): any {
    if (!this.offerFlowPaymentPlanId || !this.property?.paymentPlans) return null;
    if (this.offerFlowPaymentPlanId === '__all__') {
      return {
        label: `All Payment Plans (${this.property.paymentPlans.length})`,
        selectorLabel: `All Payment Plans (${this.property.paymentPlans.length})`,
        cycleDescription: null,
        installmentsCount: null,
        installment: null,
        reserveAmount: null,
        reservePercent: null,
        downAmount: null,
        downPercent: null,
        unitPrice: null,
        totalPrice: null,
        extraRows: []
      };
    }
    const plan = this.property.paymentPlans.find(p => p.id === this.offerFlowPaymentPlanId);
    return plan ? this.paymentPlanSummary(plan, this.property) : null;
  }

  get offerFlowCanContinueFromRecipient(): boolean {
    if (this.offerFlowRecipientMode === 'self') return true;
    if (this.offerFlowRecipientMode === 'existing') return !!this.offerFlowSelectedLeadId;
    if (this.offerFlowRecipientMode === 'new') {
      return !!(this.newLeadForm.firstName?.trim() || this.newLeadForm.lastName?.trim()) &&
        !!(this.newLeadForm.email?.trim() || this.toE164(this.newLeadForm.phone));
    }
    return false;
  }

  get offerFlowCanContinueFromConfigure(): boolean {
    return !!this.offerFlowPaymentPlanId;
  }

  // ─── Reserve Flow ─────────────────────────────────────────────────────────

  reserveFlowOpen = false;
  reserveFlowStep = 1;
  reserveFlowTotalSteps = 3;
  reserveFlowSubmitting = false;
  reserveFlowError: string | null = null;
  reserveFlowPlanId: string | null = null;
  reserveFlowClientName: string | null = null;
  reserveFlowClientEmail: string | null = null;
  reserveFlowClientPhone: any = null;
  reserveFlowOtpChannel: ReservationOtpChannel = 'phone';
  reserveFlowOtpCode = '';
  reserveFlowContactValidating = false;
  reservationSessionId = '';

  otpError: string | null = null;
  otpSending = false;
  otpTargetMasked: string | null = null;
  otpVerifying = false;
  verifiedChannel: ReservationOtpChannel | null = null;
  verifiedEmailTarget: string | null = null;
  verifiedPhoneTarget: string | null = null;

  reserveFlowResult: ReservationResult | null = null;

  // Set when createReservation fails because the unit's offer PDF can't be
  // generated (e.g. an inconsistent payment plan). The reservation row itself
  // is created, but the signing document needs a valid offer first. We surface
  // a clear message + a "Fix offer requirements" action that deep-links to the
  // offers "missing" tab with this unit's popup open.
  reserveFlowOfferRequirementMissing = false;
  // The exact backend reason (e.g. "Payment plan has inconsistent components…")
  // shown to the developer so they know precisely what to fix.
  reserveFlowOfferRequirementReason: string | null = null;

  get reserveFlowCanSubmit(): boolean {
    return !!(this.verifiedChannel && (this.verifiedEmailTarget || this.verifiedPhoneTarget));
  }

  reserveFlowStep1Invalid(): boolean {
    if (this.selectedInvestorId) return false;
    const name = String(this.reserveFlowClientName || '').trim();
    if (!name) return true;
    // Channel-aware: require the contact field matching the selected OTP channel
    // so the requirement surfaces at step 1 instead of failing later at verify.
    const email = String(this.reserveFlowClientEmail || '').trim();
    const phoneE164 = this.toE164(this.reserveFlowClientPhone);
    if (this.reserveFlowOtpChannel === 'email') {
      return !(email && email.includes('@'));
    }
    return !phoneE164;
  }

  // ─── Investor Search (Reserve Flow) ───────────────────────────────────────

  investorSearchQuery = '';
  investorSearchLoading = false;
  investorSearchDropdownOpen = false;
  investorSearchResults: InvestorSearchResult[] = [];
  selectedInvestorId: string | null = null;
  private selectedInvestor: InvestorSearchResult | null = null;

  // ─── Reserve On Behalf ────────────────────────────────────────────────────

  reserveOnBehalfOpen = false;
  reserveOnBehalfStep: 'form' | 'otp' | 'success' = 'form';
  reserveOnBehalfError: string | null = null;
  reserveOnBehalfSubmitting = false;
  reserveOnBehalfVerifying = false;
  reserveOnBehalfForm = { fullName: '', email: '', phone: '', countryCode: 'EG' };
  reserveOnBehalfOtpTarget = '';
  reserveOnBehalfOtpTargetMasked = '';
  reserveOnBehalfSessionId = '';
  reserveOnBehalfOtpCode = '';
  reserveOnBehalfResult: ReservationResult | null = null;
  reserveOnBehalfContinuing = false;

  // ─── KYC (shared across reserve flow & reserve-on-behalf) ─────────────────

  kycLoading = false;
  kycError: string | null = null;
  kyc: ReservationKycRecord | null = null;
  kycDraft: {
    full_name: string; arabic_full_name: string; id_number: string; nationality: string;
    birth_date: string; address_line1: string; address_line2: string; address_city: string; address_country: string;
  } = { full_name: '', arabic_full_name: '', id_number: '', nationality: '', birth_date: '', address_line1: '', address_line2: '', address_city: '', address_country: '' };
  private kycDraftSnapshot: { [k: string]: string } = {};
  reserveFlowKycMode: ReserveFlowKycMode = 'choose';
  kycIdUploading = false;
  kycIdBackUploading = false;
  kycSaving = false;
  kycApproving = false;
  reserveFlowKycDocumentKindLabel = '';
  signing = false;
  rechecking = false;
  pdfPreparing = false;
  pdfPreparingReservationId: string | null = null;
  pdfPreparingTitle = 'Reservation PDF';
  pdfPreparingCountdown = 0;
  pdfPreparingAttempts = 0;
  readonly pdfPreparingMaxAttempts = 12;
  pdfPreparingError: string | null = null;
  pdfPreparingErrorCode: string | null = null;
  pdfPreparingPolling = false;
  pdfPreparingStopped = false;
  private pdfPreparingTimer: ReturnType<typeof setInterval> | null = null;
  kycIdFrontPreviewUrl: string | null = null;
  kycIdFrontPreviewIsImage = false;
  kycIdBackPreviewUrl: string | null = null;
  kycIdBackPreviewIsImage = false;
  kycIdFrontFileName = '';
  kycIdBackFileName = '';

  get reserveFlowReservationId(): string | null {
    return this.reserveFlowResult?.reservationId || this.reserveOnBehalfResult?.reservationId || null;
  }

  // ─── KYC Camera ───────────────────────────────────────────────────────────

  kycCameraVisible = false;
  kycCameraSide: 'front' | 'back' = 'front';
  kycCameraCaptureUrl: string | null = null;
  kycCameraStarting = false;
  kycCameraError: string | null = null;
  private kycCameraStream: MediaStream | null = null;
  @ViewChild('kycCameraVideo') kycCameraVideo!: ElementRef<HTMLVideoElement>;

  // ─── Lead Plan Modal ──────────────────────────────────────────────────────

  leadPlanModalOpen = false;
  leadPlanSelectedId: string | null = null;
  leadPlanError: string | null = null;
  creatingLead = false;
  private leadPlanTargetLeadId: string | null = null;
  private leadPlanMode: 'create' | 'assign' = 'create';

  trackReservationId(index: number, item: ReservationRecord): string { return item.id || String(index); }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.authSub = this.store.select(selectCurrentUser).subscribe(user => {
      this.currentUser = user;
      this.currentUserEmail = user?.email ?? null;
      this.permissionCodes = Array.isArray(user?.permission_codes) ? user.permission_codes : Array.isArray(user?.permissionCodes) ? user.permissionCodes : [];
      this.postSaleFollowUpEnabled = resolvePostSaleFollowUpEnabled((user as any)?.feature_flags?.post_sale_follow_up_enabled);
    });

    // Track the actor's org so foreign-org (partner-granted) inventory hides its
    // edit/status/delete controls even when the user holds those codes for their own org.
    this.actorSub = this.store.select(selectActor).subscribe(actor => {
      this.currentUserOrgId = actor?.org_id ? String(actor.org_id) : null;
      this.viewerIsAppAdmin = String(actor?.role_code || '').trim().toLowerCase() === 'app_admin';
    });

    this.searchSub = this.searchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query.trim()) return Promise.resolve({ items: [] as InvestorSearchResult[], total: 0 });
        this.investorSearchLoading = true;
        return firstValueFrom(this.investorsService.searchInvestors({ search: query, perPage: 10 })).catch(() => ({ items: [] as InvestorSearchResult[], total: 0 }));
      })
    ).subscribe(result => {
      this.investorSearchResults = result.items;
      this.investorSearchLoading = false;
      this.investorSearchDropdownOpen = result.items.length > 0;
    });

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error = 'Missing property id';
      this.loading = false;
      return;
    }
    this.load(id);
    this.loadReservations(id);

    // When a signing dialog is dismissed, re-check that reservation's Zoho status so
    // the section flips from "Continue to sign" to "Contract signed" without a manual
    // recheck (the client may have just finished signing inside the dialog).
    this.viewerClosedSub = this.viewerDialog.closed$.subscribe((state) => {
      const reservationId = state?.reservationId ? String(state.reservationId).trim() : '';
      if (!reservationId) return;
      if (this.reserveFlowReservationId && reservationId === String(this.reserveFlowReservationId)) {
        void this.recheckReservationFlow();
      }
      const lead = this.reservations.find((x) => x.id === reservationId);
      if (lead) void this.recheckLeadReservation(lead);
    });
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.actorSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    this.viewerClosedSub?.unsubscribe();
    this.stopPdfCountdown();
    this.stopKycCameraStream();
    this.revokeKycPreview('front');
    this.revokeKycPreview('back');
  }

  // ─── Property Loading ─────────────────────────────────────────────────────

  load(id: string): void {
    this.loading = true;
    this.error = null;
    this.propertiesService.getPropertyAuth(id).subscribe({
      next: (prop) => {
        this.property = prop;
        this.imageErrors = {};
        this.imageFallbacks = {};
        this.galleryImages = this.sortImages(prop?.images || []);
        this.activeIndex = 0;
        this.offerDefaultsPlanId = prop?.offerDefaultPaymentPlanId ? String(prop.offerDefaultPaymentPlanId) : null;
        this.offerDefaultsNotes = String(prop?.offerDefaultNotes || '').trim();
        this.offerDefaultsError = null;
        // Default to first payment plan
        if (prop?.paymentPlans?.length && !this.selectedDetailPlanId) {
          this.selectedDetailPlanId = String(prop.paymentPlans[0].id || '');
        }
        if (this.postSaleFollowUpEnabled) this.loadPostSale(prop.id);
        this.loading = false;
      },
      error: () => {
        this.error = 'Unable to load property details.';
        this.property = null;
        this.galleryImages = [];
        this.activeIndex = 0;
        this.loading = false;
      }
    });
  }

  loadReservations(propertyId: string): void {
    const id = String(propertyId || '').trim();
    if (!id) return;
    this.reservationsLoading = true;
    this.reservationsError = null;
    this.reservationsService.getReservations({ propertyId: id }, { page: 1, perPage: 200 }).subscribe({
      next: (rows) => {
        this.reservationsLoading = false;
        this.reservations = Array.isArray(rows) ? rows : [];
        const allowed = new Set(this.reservations.map(r => String(r?.id || '').trim()).filter(Boolean));
        this.selectedLeadIds.forEach(leadId => {
          if (!allowed.has(leadId)) this.selectedLeadIds.delete(leadId);
        });
      },
      error: (err) => {
        this.reservationsLoading = false;
        this.reservations = [];
        this.selectedLeadIds.clear();
        this.reservationsError = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to load leads';
      }
    });
  }

  private loadPostSale(propertyId: string): void {
    this.postSaleLoading = true;
    this.postSaleService.listFollowUp({ propertyId }).subscribe({
      next: (cases) => {
        this.postSaleCases = Array.isArray(cases) ? cases : [];
        this.postSaleLoading = false;
      },
      error: () => {
        this.postSaleCases = [];
        this.postSaleLoading = false;
      }
    });
  }

  // ─── Offer Defaults ───────────────────────────────────────────────────────

  async saveOfferDefaults(): Promise<void> {
    if (this.savingOfferDefaults) return;
    const propertyId = String(this.property?.id || '').trim();
    if (!propertyId) return;
    const planId = String(this.offerDefaultsPlanId || '').trim() || null;
    const notes = String(this.offerDefaultsNotes || '').trim() || null;
    this.savingOfferDefaults = true;
    this.offerDefaultsError = null;
    try {
      const updated = await firstValueFrom(
        this.propertiesService.updateOfferDefaults(propertyId, {
          offer_default_payment_plan_id: planId,
          offer_default_notes: notes
        })
      );
      this.property = updated;
      this.offerDefaultsPlanId = updated?.offerDefaultPaymentPlanId ? String(updated.offerDefaultPaymentPlanId) : null;
      this.offerDefaultsNotes = String(updated?.offerDefaultNotes || '').trim();
      this.notifications.showSuccess('Offer defaults saved').subscribe();
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to save offer defaults';
      this.offerDefaultsError = msg;
      this.notifications.showError(msg).subscribe();
    } finally {
      this.savingOfferDefaults = false;
    }
  }

  // ─── Bulk Send Offers ─────────────────────────────────────────────────────

  isLeadSelected(leadId: string): boolean {
    const id = String(leadId || '').trim();
    return Boolean(id && this.selectedLeadIds.has(id));
  }

  toggleLead(leadId: string, checked: boolean): void {
    const id = String(leadId || '').trim();
    if (!id) return;
    if (checked) this.selectedLeadIds.add(id); else this.selectedLeadIds.delete(id);
    this.bulkSendResult = null;
  }

  selectAllLeads(): void {
    (this.reservations || []).forEach(r => {
      const id = String(r?.id || '').trim();
      if (id) this.selectedLeadIds.add(id);
    });
    this.bulkSendResult = null;
  }

  clearLeadSelection(): void {
    this.selectedLeadIds.clear();
    this.bulkSendResult = null;
  }

  async sendOffersToSelected(): Promise<void> {
    if (this.sendingOffers) return;
    const propertyId = String(this.property?.id || '').trim();
    if (!propertyId) return;
    const leadIds = Array.from(this.selectedLeadIds.values());
    if (!leadIds.length) return;
    const planId = String(this.offerDefaultsPlanId || '').trim() || String(this.property?.paymentPlans?.[0]?.id || '').trim() || null;
    if (!planId) {
      this.notifications.showInfo('This unit has no payment plans. Add one before sending offers.').subscribe();
      return;
    }
    const ok = await this.confirm.confirm({
      title: 'Send offer(s)?',
      message: `Send offers for this unit to ${leadIds.length} lead(s)?`,
      confirmText: 'Send',
      cancelText: 'Cancel'
    });
    if (!ok) return;
    this.sendingOffers = true;
    this.bulkSendResult = null;
    try {
      const result = await firstValueFrom(this.offersService.bulkSendByProperty(propertyId, leadIds, { payment_plan_id: planId }));
      this.bulkSendResult = result;
      this.notifications.showSuccess(`Sent ${result.sent} offer(s). Skipped ${result.skipped}.`).subscribe();
      if (result.sent > 0 && result.errors.length === 0) this.selectedLeadIds.clear();
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to send offers';
      this.notifications.showError(msg).subscribe();
    } finally {
      this.sendingOffers = false;
    }
  }

  // ─── Status Change ────────────────────────────────────────────────────────

  changePropertyStatus(status: string): void {
    if (!this.property?.id || this.updatingStatus) return;
    if (!this.canEditStatus) {
      this.notifications.showAccessRestricted().subscribe();
      return;
    }
    this.updatingStatus = true;
    this.propertiesService.updatePropertyStatus(this.property.id, status).subscribe({
      next: (result) => {
        this.property = { ...this.property!, status: result?.status || status };
        this.updatingStatus = false;
        this.notifications.showSuccess('Status updated').subscribe();
      },
      error: (err) => {
        this.updatingStatus = false;
        this.notifications.showError(err?.message || 'Failed to update status').subscribe();
      }
    });
  }

  // ─── Gallery / Images ─────────────────────────────────────────────────────

  get activeHeroImage(): Property['images'][number] | null {
    const images = this.galleryImages || [];
    if (!images.length) return null;
    const idx = Math.min(Math.max(0, this.activeIndex), images.length - 1);
    return images[idx] || null;
  }

  setActiveIndex(index: number): void {
    const images = this.galleryImages || [];
    if (!images.length) return;
    this.activeIndex = Math.min(Math.max(0, index), images.length - 1);
  }

  private sortImages(images: Property['images']): Property['images'] {
    return [...(images || [])].sort((a, b) => {
      const main = Number(Boolean(b?.isMain)) - Number(Boolean(a?.isMain));
      if (main !== 0) return main;
      const orderA = Number.isFinite(a?.order as number) ? Number(a.order) : 0;
      const orderB = Number.isFinite(b?.order as number) ? Number(b.order) : 0;
      return orderA - orderB;
    });
  }

  imageUrl(url?: string | null): string {
    if (!url) return this.placeholderImage;
    const fallback = this.imageFallbacks[url];
    if (fallback) return fallback;
    if (this.imageErrors[url]) return this.placeholderImage;
    const lowered = url.toLowerCase();
    if (lowered.includes('pending.local') || (!lowered.startsWith('http://') && !lowered.startsWith('https://'))) return this.placeholderImage;
    return url;
  }

  onImageError(url?: string | null): void {
    if (!url) return;
    if (this.imageFallbacks[url]) {
      delete this.imageFallbacks[url];
      this.imageErrors[url] = true;
      return;
    }
    const fallback = stratusProdFallbackUrl(url);
    if (fallback) { this.imageFallbacks[url] = fallback; return; }
    this.imageErrors[url] = true;
  }

  openImage(index: number): void {
    if (!this.galleryImages.length) return;
    this.modalImages = [...this.galleryImages];
    this.modalImageIndex = Math.min(Math.max(index, 0), this.modalImages.length - 1);
    this.showImageModal = true;
  }

  closeImage(): void {
    this.showImageModal = false;
    this.modalImageIndex = 0;
    this.modalImages = [];
  }

  // ─── Delete ───────────────────────────────────────────────────────────────

  confirmDelete(): void {
    if (!this.property?.id || this.deleting) return;
    this.showDeleteModal = true;
    this.deleteError = null;
  }

  cancelDelete(): void { this.showDeleteModal = false; }

  performDelete(): void {
    if (!this.property?.id || this.deleting) return;
    this.deleting = true;
    this.deleteError = null;
    this.propertiesService.softDeleteProperty(this.property.id).subscribe({
      next: () => {
        this.deleting = false;
        this.showDeleteModal = false;
        this.router.navigate(['/developer/properties']);
      },
      error: (err) => {
        this.deleting = false;
        this.deleteError = err?.message || 'Failed to delete property';
      }
    });
  }

  // ─── Share Link ───────────────────────────────────────────────────────────

  async copyShareLink(propertyId: string): Promise<void> {
    let origin = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      if (origin) {
        const u = new URL(origin);
        const hostLower = u.hostname.toLowerCase();
        const isLocal = hostLower.includes('localhost') || hostLower.startsWith('127.') || hostLower.startsWith('0.0.0.0');
        if (!isLocal && u.protocol !== 'https:') { u.protocol = 'https:'; origin = u.origin; }
      }
    } catch {}
    const url = `${origin}/server/Public/properties/${encodeURIComponent(propertyId)}/preview`;
    try {
      await navigator.clipboard.writeText(url);
      this.notifications.showSuccess('Share link copied').subscribe();
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        this.notifications.showSuccess('Share link copied').subscribe();
      } catch {
        this.notifications.showError('Failed to copy link').subscribe();
      }
    }
  }

  // ─── Viewer ───────────────────────────────────────────────────────────────

  openViewer(url: string | null | undefined, title: string, event?: Event): void {
    event?.stopPropagation();
    const projectId = this.property?.project_id ?? undefined;
    this.viewerDialog.openFromUrl(url, title, projectId);
  }

  goToLeadProfile(id: string): void {
    this.router.navigate(['/developer/leads', id]);
  }

  // ─── Formatting Helpers ───────────────────────────────────────────────────

  formatCurrency(amount?: number | null, from?: string | null): string | null {
    if (amount === null || amount === undefined || !Number.isFinite(amount)) return null;
    const code = from || this.property?.currency || 'USD';
    const display = this.currencyService.convertForDisplay(amount, code);
    const converted = display.amount;
    return this.currencyService.format(converted, display.currency);
  }

  priceParts(property: Property): { currency: string; amount: string } | null {
    const value = property?.publicDisplayPrice ?? property?.price;
    if (value === null || value === undefined || !Number.isFinite(value)) return null;
    const fromCode = (property?.currencyInfo?.code || property?.currency || 'USD').toString().trim() || 'USD';
    const display = this.currencyService.convertForDisplay(value, fromCode);
    const converted = display.amount;
    const currencyCode = display.currency;
    try {
      const parts = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'code',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).formatToParts(converted);
      const currency = (parts.find(p => p.type === 'currency')?.value || currencyCode)
        .replace(/[\u00A0\u202F]/g, ' ')
        .trim();
      const amount = parts
        .filter(p => p.type !== 'currency')
        .map(p => p.value)
        .join('')
        .replace(/[\u00A0\u202F]/g, ' ')
        .trim();
      return { currency, amount };
    } catch {
      const formatted = this.currencyService.format(converted, currencyCode);
      const match = formatted.match(/^(\S+)\s+(.+)$/);
      if (match) return { currency: match[1], amount: match[2] };
      return { currency: currencyCode, amount: formatted };
    }
  }

  isFundingProperty(property: Property): boolean {
    return isFundingDevelopmentBadge(property?.developmentBadge);
  }

  developmentHeading(property: Property): string {
    return propertyDevelopmentHeading(property?.developmentBadge);
  }

  availabilityLabel(property: Property): string {
    return propertyDevelopmentLabel(property?.readyToMove, property?.developmentBadge);
  }

  formatDeliveryDate(value?: string | null): string | null {
    const raw = (value || '').toString().trim();
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  locationLabel(property: Property): string | null {
    const parts = [property?.location, property?.countryName || property?.country].map(v => (v || '').toString().trim()).filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }

  formatNumber(value?: number | null): string | null {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    try { return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n).replace(/[\u00A0\u202F]/g, ' '); }
    catch { return String(Math.round(n)); }
  }

  floorLabel(value?: string | null): string | null {
    if (!value) return null;
    const v = String(value).trim();
    if (!v) return null;
    const lower = v.toLowerCase();
    if (lower === '0' || lower === '0.0' || lower === 'gf' || lower === 'g' || lower.includes('ground')) return 'Ground floor';
    const numericMatch = lower.match(/^(?:floor\s*)?(\d+)(?:st|nd|rd|th)?(?:\s*floor)?$/);
    if (numericMatch?.[1]) { const n = parseInt(numericMatch[1], 10); if (Number.isFinite(n)) return `Floor ${n}`; }
    const wordMap: Record<string, number> = { first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8, ninth: 9, tenth: 10, eleventh: 11, twelfth: 12 };
    const wordMatch = lower.match(/^(?:the\s+)?(\w+)(?:\s+floor)?$/);
    if (wordMatch?.[1] && wordMap[wordMatch[1]]) return `Floor ${wordMap[wordMatch[1]]}`;
    return v;
  }

  buildingNumber(unitNumber: string | undefined | null): string | null {
    if (!unitNumber) return null;
    const parts = String(unitNumber).split(/[-\/]/);
    return parts[0]?.trim() || null;
  }

  mapLink(p: Property): string | null {
    const loc = p?.location;
    if (!loc) return null;
    return `https://maps.google.com/maps?q=${encodeURIComponent(loc)}`;
  }

  // Item 5: a draft unit stays VISIBLE to the people working it, but carries no
  // status pill — `properties.status` mixes lifecycle with sale status, so a
  // draft row has no sale status to show. Suppress the pill, do not blank it.
  showStatusBadge(status: Property['status'] | Property['statusBadge']): boolean {
    return shouldShowPropertyStatusBadge(status, this.viewerIsAppAdmin);
  }

  statusMeta(status: Property['status'] | Property['statusBadge']): { label: string; className: string } {
    const key = (status || 'unknown').toString().toLowerCase().trim();
    const map: Record<string, { label: string; className: string }> = {
      available: { label: 'Available', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      reserved: { label: 'Reserved', className: 'bg-amber-50 text-amber-800 border-amber-200' },
      'reserved/hold': { label: 'Reserved / Hold', className: 'bg-amber-50 text-amber-800 border-amber-200' },
      'reserved-hold': { label: 'Reserved / Hold', className: 'bg-amber-50 text-amber-800 border-amber-200' },
      hold: { label: 'On Hold', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
      sold: { label: 'Sold', className: 'bg-red-50 text-red-700 border-red-200' },
      draft: { label: 'Draft', className: 'bg-slate-50 text-slate-700 border-slate-200' },
      unpublished: { label: 'Unpublished', className: 'bg-slate-50 text-slate-700 border-slate-200' },
      unknown: { label: 'Status Unknown', className: 'bg-slate-50 text-slate-700 border-slate-200' }
    };
    return map[key] || map['unknown'];
  }

  planLabel(plan: PaymentPlan): string {
    if (!plan?.totalPeriod) return 'Installment Plan';
    const years = plan.totalPeriod / 12;
    if (years >= 1) { const display = Number.isInteger(years) ? years : parseFloat(years.toFixed(1)); return `${display} Year Plan`; }
    return `${plan.totalPeriod} Month${plan.totalPeriod !== 1 ? 's' : ''} Plan`;
  }

  cycleLabel(value?: number | null): string | null {
    const raw = Number(value);
    if (!Number.isFinite(raw) || raw <= 0) return null;
    const monthsRaw = raw > 24 ? raw / 30 : raw;
    const months = parseFloat(monthsRaw.toFixed(1));
    if (!Number.isFinite(months) || months <= 0) return null;
    return `${months} month${months === 1 ? '' : 's'}`;
  }

  paymentPlanSummary(plan: PaymentPlan, prop: Property | null): any {
    const totalPeriod = plan?.totalPeriod;
    const paymentCycle = plan?.paymentCycle;
    const downPayment = plan?.downPayment;
    const downPaymentPercent = plan?.downPaymentPercent;
    const reservationAmount = plan?.reservationAmount;
    const reservationPercent = plan?.reservationPercent;
    const installmentAmount = plan?.installmentAmount;
    const numberOfInstallments = plan?.numberOfInstallments;
    const displayPrice = plan?.displayPrice;
    const currency = prop?.currency || 'USD';
    const fmt = (v: number | null | undefined) => (v != null && Number.isFinite(v)) ? this.formatCurrency(v, currency) : null;
    const years = totalPeriod ? totalPeriod / 12 : null;
    const label = years && years >= 1
      ? `${Number.isInteger(years) ? years : parseFloat(years.toFixed(1))} Year Plan`
      : (totalPeriod ? `${totalPeriod} Month Plan` : 'Payment Plan');
    const selectorLabel = plan?.label || label;
    const cycleDescription = paymentCycle ? this.cycleLabel(paymentCycle) : null;
    const extraRows: { label: string; value: string }[] = [];
    const rawTerms = String(plan?.paymentTerms || '').trim();
    if (rawTerms) {
      rawTerms.split(/\r?\n/g).map(l => l.trim()).filter(Boolean).forEach(line => {
        const idx = line.indexOf(':');
        if (idx === -1) extraRows.push({ label: 'Term', value: line });
        else extraRows.push({ label: line.slice(0, idx).trim() || 'Term', value: line.slice(idx + 1).trim() || '-' });
      });
    }
    const num = (v: any): number | null => typeof v === 'number' ? v : v != null && v !== '' ? Number(v) : null;
    const rawInstallment = num(installmentAmount);
    const rawInstallments = num(numberOfInstallments);
    const rawDown = num(downPayment);
    const rawReserve = num(reservationAmount);
    const computedTotal = (rawInstallment != null && rawInstallments != null ? rawInstallment * rawInstallments : 0)
      + (rawDown ?? 0)
      + (rawReserve ?? 0);

    // Always show reservation alongside down payment and total due
    return {
      label,
      selectorLabel,
      cycleDescription,
      installmentsCount: numberOfInstallments || null,
      installment: fmt(installmentAmount),
      reserveAmount: fmt(reservationAmount),
      reservePercent: reservationPercent ? `${reservationPercent}%` : null,
      downAmount: fmt(downPayment),
      downPercent: downPaymentPercent ? `${downPaymentPercent}%` : null,
      unitPrice: fmt(prop?.price),
      totalPrice: computedTotal > 0 ? fmt(computedTotal) : fmt(displayPrice),
      totalDue: computedTotal > 0 ? fmt(computedTotal) : fmt(displayPrice),
      extraRows
    };
  }

  displayLeadStatusLabel(status: string): string {
    if (!status) return 'Pending';
    const s = status.toLowerCase().trim();
    const map: Record<string, string> = {
      pending: 'Pending', qualified: 'Qualified', contacted: 'Contacted',
      negotiation: 'Negotiation', 'offer-sent': 'Offer Sent', 'offer-accepted': 'Offer Accepted',
      reserved: 'Reserved', sold: 'Sold', lost: 'Lost', cancelled: 'Cancelled',
      'on-hold': 'On Hold', hold: 'On Hold', active: 'Active', new: 'New',
      'cold': 'Cold', 'warm': 'Warm', 'hot': 'Hot',
      'self_requested_offer': 'Requested Sales Offer'
    };
    return map[s] || status.charAt(0).toUpperCase() + status.slice(1);
  }

  isReservedLeadStatus(status: string | null | undefined): boolean {
    const s = String(status || '').toLowerCase().trim();
    return ['reserved', 'reserved_with_deposit', 'reservedwithdeposit', 'sold', 'contracted'].includes(s);
  }

  private readonly MISSING_FIELD_LABELS: Record<string, string> = {
    full_name: 'Full name (English)',
    arabic_full_name: 'Full name (Arabic)',
    id_number: 'ID number',
    nationality: 'Nationality',
    birth_date: 'Date of birth',
    id_document_back: 'ID document (back side)'
  };

  missingFieldsLabel(fields: string[] | null | undefined): string {
    const arr = Array.isArray(fields) ? fields : [];
    if (!arr.length) return '';
    return arr.map((f) => this.MISSING_FIELD_LABELS[f] || f).join(', ');
  }

  postSaleStatusClass(status: string): string {
    if (!status) return '';
    const s = status.toLowerCase().trim();
    if (s.includes('overdue')) return 'bg-red-50 text-red-700';
    if (s.includes('due')) return 'bg-amber-50 text-amber-700';
    if (s.includes('complete') || s.includes('done')) return 'bg-emerald-50 text-emerald-700';
    if (s.includes('active')) return 'bg-blue-50 text-blue-700';
    return 'bg-slate-50 text-slate-700';
  }

  postSaleStatusLabel(status: string): string {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1).replace(/[-_]/g, ' ');
  }

  paymentTermItems(plan: PaymentPlan): Array<{ label: string; value: string }> {
    const raw = String(plan?.paymentTerms || '').trim();
    if (!raw) return [];
    return raw.split(/\r?\n/g).map(l => l.trim()).filter(Boolean).map(line => {
      const idx = line.indexOf(':');
      if (idx === -1) return { label: 'Term', value: line };
      return { label: line.slice(0, idx).trim() || 'Term', value: line.slice(idx + 1).trim() || '-' };
    });
  }

  // ─── Offer Flow ───────────────────────────────────────────────────────────

  openOfferFlow(onlyEmail: boolean = false): void {
    this.offerFlowOpen = true;
    this.offerFlowStep = 1;
    this.offerFlowError = null;
    this.offerFlowSubmitting = false;
    this.offerFlowRecipientMode = null;
    this.offerFlowSelectedLeadId = null;
    this.offerFlowCreatedLeadId = null;
    this.offerFlowPaymentPlanId = this.offerDefaultsPlanId || (this.property?.paymentPlans?.[0]?.id ?? null);
    this.offerReviewUrl = null;
    this.offerReviewError = null;
    this.offerReviewLoading = false;
    this.offerReviewConfirmed = false;
    this.newLeadForm = { firstName: '', lastName: '', email: '', phone: { countryCode: 'EG', phone: '' } };
    this.offerEmailDisabledReason = onlyEmail ? null : null;
  }

  closeOfferFlow(): void { this.offerFlowOpen = false; }

  offerFlowStepTitle(): string {
    const titles: Record<number, string> = { 1: 'Recipient', 2: 'Payment Plan', 3: 'Review', 4: 'Send' };
    return titles[this.offerFlowStep] || 'Step';
  }

  offerFlowStepDescription(): string {
    const descs: Record<number, string> = {
      1: 'Choose who will receive this offer',
      2: 'Select a payment plan for this offer',
      3: 'Preview the offer before sending',
      4: 'Confirm and send the offer'
    };
    return descs[this.offerFlowStep] || '';
  }

  chooseOfferRecipient(mode: OfferFlowRecipientMode): void {
    if (this.offerFlowRecipientMode !== mode) this.resetOfferReviewConfirmation();
    this.offerFlowRecipientMode = mode;
    this.offerFlowSelectedLeadId = null;
    this.offerFlowCreatedLeadId = null;
  }

  selectOfferFlowLead(id: string): void {
    if (this.offerFlowSelectedLeadId !== id) this.resetOfferReviewConfirmation();
    this.offerFlowSelectedLeadId = id;
  }

  onOfferFlowPaymentPlanChanged(): void {
    this.resetOfferReviewConfirmation();
  }

  onOfferFlowNewLeadEdited(): void {
    this.offerFlowCreatedLeadId = null;
    this.resetOfferReviewConfirmation();
  }

  resetOfferReviewConfirmation(): void {
    this.offerReviewConfirmed = false;
  }

  offerRecipientLabel(): string {
    if (this.offerFlowRecipientMode === 'self') return this.currentUser?.name || this.currentUserEmail || 'You';
    if (this.offerFlowRecipientMode === 'existing') {
      const lead = this.reservations.find(r => r.id === this.offerFlowSelectedLeadId);
      return lead?.customer || lead?.email || 'Selected lead';
    }
    if (this.offerFlowRecipientMode === 'new') {
      const n = [this.newLeadForm.firstName, this.newLeadForm.lastName].filter(Boolean).join(' ');
      return n || 'New client';
    }
    return 'Select recipient';
  }

  offerRecipientEmail(): string | null {
    if (this.offerFlowRecipientMode === 'self') return this.currentUserEmail;
    if (this.offerFlowRecipientMode === 'existing') {
      const lead = this.reservations.find(r => r.id === this.offerFlowSelectedLeadId);
      return lead?.email || null;
    }
    if (this.offerFlowRecipientMode === 'new') return this.newLeadForm.email?.trim() || null;
    return null;
  }

  backOfferFlowStep(): void {
    if (this.offerFlowStep > 1) this.offerFlowStep--;
  }

  returnOfferFlowToReview(): void {
    this.offerFlowStep = 3;
  }

  async continueOfferFlowFromRecipientStep(): Promise<void> {
    if (!this.offerFlowCanContinueFromRecipient || this.offerFlowSubmitting) return;
    this.offerFlowSubmitting = true;
    this.offerFlowError = null;
    try {
      if (this.offerFlowRecipientMode === 'new' && !this.offerFlowCreatedLeadId) {
        const phone = this.toE164(this.newLeadForm.phone);
        const lead = await firstValueFrom(this.leadsService.createManualLead({
          propertyId: this.property?.id || '',
          fullName: [this.newLeadForm.firstName, this.newLeadForm.lastName].filter(Boolean).join(' '),
          email: this.newLeadForm.email?.trim() || undefined,
          phone: phone || undefined,
          status: 'new'
        }));
        this.offerFlowCreatedLeadId = lead.leadId;
        this.offerFlowSelectedLeadId = lead.leadId;
      }
      this.offerFlowStep = 2;
    } catch (err: any) {
      this.offerFlowError = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to create lead';
    } finally {
      this.offerFlowSubmitting = false;
    }
  }

  continueOfferFlowFromConfigureStep(): void {
    if (!this.offerFlowCanContinueFromConfigure || this.offerFlowSubmitting) return;
    this.offerFlowStep = 3; // Go to review step
  }

  async continueOfferFlowFromReviewStep(): Promise<void> {
    if (!this.offerFlowCanContinueFromConfigure || this.offerFlowSubmitting) return;
    this.offerFlowStep = 4; // Go to send step
  }

  async previewOfferFlow(): Promise<void> {
    if (this.offerReviewLoading) return;
    this.offerReviewLoading = true;
    this.offerReviewError = null;
    this.offerReviewUrl = null;
    try {
      // Single payment plan → preview the OFFER PDF (the same PDF that gets sent).
      // "All Payment Plans" → preview the PAYMENT PLANS comparison PDF.
      const isAllPlans = this.offerFlowPaymentPlanId === '__all__';
      const recipientEmail = this.offerRecipientEmail() || undefined;
      const blob = isAllPlans
        ? await firstValueFrom(this.offersService.previewPaymentPlansPdf(this.property?.id || '', null, recipientEmail))
        : await firstValueFrom(this.offersService.previewPropertyPdf(
            this.property?.id || '',
            this.offerFlowPaymentPlanId,
            recipientEmail
          ));
      this.offerReviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));
    } catch (err: any) {
      this.offerReviewError = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to generate preview';
    } finally {
      this.offerReviewLoading = false;
    }
  }

  retryOfferReview(): void { this.previewOfferFlow(); }

  offerFlowSendSummary(): string {
    if (this.offerFlowPaymentPlanId === '__all__') {
      return `Send offer with all payment plans to ${this.offerRecipientLabel()}`;
    }
    const planLabel = this.offerFlowSelectedPlanSummary?.label || 'selected plan';
    return `Send offer with ${planLabel} to ${this.offerRecipientLabel()}`;
  }

  async sendOfferEmailInBackground(): Promise<void> {
    if (this.offerFlowSubmitting || !this.offerFlowCanContinueFromConfigure || !this.offerReviewConfirmed) return;
    this.offerFlowSubmitting = true;
    this.offerFlowError = null;
    let offerId: string | null = null;
    try {
      const propertyId = String(this.property?.id || '').trim();
      if (!propertyId) throw new Error('No property loaded');
      const isAllPlans = this.offerFlowPaymentPlanId === '__all__';
      const result = await firstValueFrom(this.offersService.requestOffer(propertyId, {
        payment_plan_id: isAllPlans ? '__all__' : this.offerFlowPaymentPlanId,
        send_email: false
      }));

      offerId = result?.offer_id ? String(result.offer_id) : null;
      if (offerId) {
        await firstValueFrom(this.offersService.send(offerId, {
          send_email: true,
          recipient_email: this.offerRecipientEmail() || undefined,
          review_confirmed: true,
          reviewConfirmed: true
        }));
      }
      this.notifications.showSuccess('Offer sent.').subscribe();
      this.closeOfferFlow();
      this.loadReservations(propertyId);
    } catch (err: any) {
      const code = String(err?.error?.error?.code || err?.error?.code || '').trim().toUpperCase();
      if (offerId && code === 'OFFER_APPROVAL_REQUIRED') {
        try {
          await firstValueFrom(this.offerApprovalsService.submit(offerId));
          this.closeOfferFlow();
          this.notifications.showSuccess('Offer submitted for approval. It will be emailed after approval.').subscribe();
          this.loadReservations(String(this.property?.id || '').trim());
          return;
        } catch (approvalErr: any) {
          this.offerFlowError = this.apiError(approvalErr, 'Unable to submit this offer for approval.');
          return;
        }
      }
      if (code === 'OFFER_APPROVAL_PENDING') {
        this.closeOfferFlow();
        this.notifications.showInfo('This offer is already waiting for approval. It will be emailed after approval.').subscribe();
        return;
      }
      this.offerFlowError = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to send offer';
    } finally {
      this.offerFlowSubmitting = false;
    }
  }

  // ─── Reserve Flow ─────────────────────────────────────────────────────────

  openReserveFlow(): void {
    this.reserveFlowOpen = true;
    this.reserveFlowStep = 1;
    this.reserveFlowError = null;
    this.reserveFlowResult = null;
    this.reserveFlowOfferRequirementMissing = false;
    this.reserveFlowOfferRequirementReason = null;
    this.reserveFlowPlanId = this.offerDefaultsPlanId || (this.property?.paymentPlans?.[0]?.id ?? null);
    this.reserveFlowClientName = null;
    this.reserveFlowClientEmail = null;
    this.reserveFlowClientPhone = null;
    this.reserveFlowOtpChannel = 'phone';
    this.reserveFlowOtpCode = '';
    this.reserveFlowSubmitting = false;
    this.reserveFlowContactValidating = false;
    this.reservationSessionId = '';
    this.otpError = null;
    this.otpSending = false;
    this.otpTargetMasked = null;
    this.otpVerifying = false;
    this.verifiedChannel = null;
    this.verifiedEmailTarget = null;
    this.verifiedPhoneTarget = null;
    this.investorSearchQuery = '';
    this.investorSearchResults = [];
    this.investorSearchDropdownOpen = false;
    this.selectedInvestorId = null;
    this.selectedInvestor = null;
    this.resetKycState();
  }

  closeReserveFlow(): void {
    this.reserveFlowOpen = false;
    this.reserveOnBehalfOpen = false;
    this.resetKycState();
    this.stopKycCameraStream();
  }

  setReserveFlowStep(step: number): void { this.reserveFlowStep = step; }

  // Deep-link to the offers "missing" tab and open this unit's popup so the
  // developer can fix the offer requirements (e.g. correct the payment plan)
  // that blocked the signing document. The offers page reads `property_id` to
  // auto-open the unit dialog.
  navigateToFixOfferRequirements(): void {
    const id = String(this.property?.id || '').trim();
    if (!id) return;
    this.closeReserveFlow();
    void this.router.navigate(['/developer/offers', 'missing'], { queryParams: { property_id: id } });
  }

  onReserveFlowFieldEdited(): void {
    this.reserveFlowError = null;
    this.otpError = null;
  }

  // ─── Investor Search (Reserve Flow Step 1) ────────────────────────────────

  onInvestorSearchInput(query: string): void {
    this.investorSearchQuery = query;
    if (this.selectedInvestorId) {
      this.selectedInvestorId = null;
      this.selectedInvestor = null;
      this.reserveFlowClientName = null;
      this.reserveFlowClientEmail = null;
      this.reserveFlowClientPhone = null;
    }
    this.searchInput$.next(query);
  }

  onInvestorSearchBlur(): void {
    setTimeout(() => { this.investorSearchDropdownOpen = false; }, 200);
  }

  selectInvestor(inv: InvestorSearchResult): void {
    this.selectedInvestorId = inv.id;
    this.selectedInvestor = inv;
    this.investorSearchQuery = inv.fullName || inv.email || inv.id;
    this.investorSearchDropdownOpen = false;
    this.reserveFlowClientName = inv.fullName || null;
    this.reserveFlowClientEmail = inv.email || null;
    this.reserveFlowClientPhone = inv.phone ? this.fromE164(inv.phone) : null;
  }

  clearInvestorSelection(): void {
    this.selectedInvestorId = null;
    this.selectedInvestor = null;
    this.investorSearchQuery = '';
    this.investorSearchResults = [];
    this.investorSearchDropdownOpen = false;
    this.reserveFlowClientName = null;
    this.reserveFlowClientEmail = null;
    this.reserveFlowClientPhone = null;
  }

  trackInvestorId(index: number, item: InvestorSearchResult): string { return item.id; }

  // ─── Reserve Flow Step 1 → OTP ────────────────────────────────────────────

  async advanceFromStep1(): Promise<void> {
    if (this.reserveFlowStep1Invalid() || this.reserveFlowContactValidating) return;

    if (this.selectedInvestorId) {
      // For a previously-added investor, create a reservation session first
      // so the backend uses the investor's user_id and skips identity eligibility checks.
      this.reserveFlowContactValidating = true;
      this.reserveFlowError = null;
      try {
        const phoneE164 = this.toE164(this.reserveFlowClientPhone);
        const sessionData = await firstValueFrom(this.reservationSessionService.createInvestorSession({
          investorUserId: this.selectedInvestorId,
          propertyId: this.property?.id || null,
          paymentPlanId: this.reserveFlowPlanId || null,
          fullName: this.reserveFlowClientName?.trim() || null,
          email: this.reserveFlowClientEmail?.trim() || null,
          phone: phoneE164 || null
        }));
        this.reservationSessionId = String(sessionData.fast_reservation_session_id || '').trim();
        // The investor_selection channel means the org actor vouches for the investor's identity.
        // Set verifiedChannel based on which contact info is available so the lead record
        // reflects the best available contact method.
        if (sessionData.verified_channel === 'investor_selection') {
          const hasPhone = !!(sessionData.phone || phoneE164);
          const hasEmail = !!(sessionData.email || this.reserveFlowClientEmail?.trim());
          this.verifiedChannel = hasPhone ? 'phone' : hasEmail ? 'email' : 'phone';
          this.verifiedPhoneTarget = hasPhone ? (sessionData.phone || phoneE164 || null) : null;
          this.verifiedEmailTarget = hasEmail ? (sessionData.email || this.reserveFlowClientEmail?.trim() || null) : null;
        } else if (sessionData.verified_channel === 'email') {
          this.verifiedChannel = 'email';
          this.verifiedEmailTarget = sessionData.verified_target || sessionData.email || null;
        } else if (sessionData.verified_channel === 'phone') {
          this.verifiedChannel = 'phone';
          this.verifiedPhoneTarget = sessionData.verified_target || sessionData.phone || null;
        }
        await this.submitReserveFlow();
      } catch (err: any) {
        this.reserveFlowError = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to create reservation session';
      } finally {
        this.reserveFlowContactValidating = false;
      }
      return;
    }

    this.reserveFlowContactValidating = true;
    this.reserveFlowError = null;
    try {
      const phoneE164 = this.toE164(this.reserveFlowClientPhone);
      const validation = await firstValueFrom(this.reservationSessionService.validateContact({
        email: this.reserveFlowClientEmail?.trim() || null,
        phone: phoneE164 || null,
        propertyId: this.property?.id || null,
        paymentPlanId: this.reserveFlowPlanId || null,
        fullName: this.reserveFlowClientName?.trim() || null,
        sessionId: this.reservationSessionId || null
      }));
      if (validation.ok) {
        this.reserveFlowStep = 2;
        await this.sendReserveFlowOtp();
      } else {
        this.reserveFlowError = 'Contact validation failed. Please check the details.';
      }
    } catch (err: any) {
      this.reserveFlowError = err?.error?.error?.message || err?.error?.message || err?.message || 'Validation failed';
    } finally {
      this.reserveFlowContactValidating = false;
    }
  }

  // ─── Reserve Flow OTP ─────────────────────────────────────────────────────

  async sendReserveFlowOtp(): Promise<void> {
    if (this.otpSending || this.otpVerifying || this.reserveFlowSubmitting) return;
    this.otpError = null;
    const channel = this.reserveFlowOtpChannel;
    const target = this.currentVerificationTarget(channel);
    if (!target) {
      this.otpError = channel === 'email' ? 'Enter the investor email first.' : 'Enter the investor phone first.';
      return;
    }
    this.otpSending = true;
    try {
      const phoneE164 = this.toE164(this.reserveFlowClientPhone);
      const data = await firstValueFrom(this.reservationSessionService.sendOtp({
        channel,
        email: this.reserveFlowClientEmail?.trim() || null,
        phone: phoneE164 || null,
        propertyId: this.property?.id || null,
        paymentPlanId: this.reserveFlowPlanId || null,
        fullName: this.reserveFlowClientName?.trim() || null,
        sessionId: this.reservationSessionId || null
      }));
      this.reservationSessionId = String(data.fast_reservation_session_id || '').trim();
      this.otpTargetMasked = data.target_masked || target;
      this.reserveFlowOtpCode = '';
      this.notifications.showSuccess(`OTP sent to ${this.otpTargetMasked}.`).subscribe();
    } catch (err: any) {
      this.otpError = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to send OTP';
    } finally {
      this.otpSending = false;
    }
  }

  async verifyReserveFlowOtp(): Promise<void> {
    if (this.otpVerifying || this.otpSending || this.reserveFlowSubmitting) return;
    this.otpError = null;
    const channel = this.reserveFlowOtpChannel;
    const target = this.currentVerificationTarget(channel);
    const code = String(this.reserveFlowOtpCode || '').trim();
    if (!target) { this.otpError = 'Missing contact target'; return; }
    if (!code) { this.otpError = 'Enter the OTP code.'; return; }
    this.otpVerifying = true;
    try {
      const phoneE164 = this.toE164(this.reserveFlowClientPhone);
      const data = await firstValueFrom(this.reservationSessionService.verifyOtp({
        channel,
        code,
        email: this.reserveFlowClientEmail?.trim() || null,
        phone: phoneE164 || null,
        propertyId: this.property?.id || null,
        paymentPlanId: this.reserveFlowPlanId || null,
        fullName: this.reserveFlowClientName?.trim() || null,
        sessionId: this.reservationSessionId || null
      }));
      this.reservationSessionId = String(data.fast_reservation_session_id || '').trim();
      this.verifiedChannel = data.channel;
      if (data.channel === 'email') this.verifiedEmailTarget = data.target || target;
      else this.verifiedPhoneTarget = data.target || target;
      this.notifications.showSuccess(`${data.channel === 'email' ? 'Email' : 'Phone'} verified.`).subscribe();
      // Auto-submit the reservation after successful OTP verification
      await this.submitReserveFlow();
    } catch (err: any) {
      this.clearVerificationState(channel);
      this.otpError = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to verify OTP';
    } finally {
      this.otpVerifying = false;
    }
  }

  // ─── Reserve Flow Submit ──────────────────────────────────────────────────

  async submitReserveFlow(): Promise<void> {
    if (!this.reserveFlowCanSubmit && !this.selectedInvestorId) return;
    if (this.reserveFlowSubmitting) return;

    // NOTE: no pre-check on property status — multiple users are allowed to reserve the
    // same unit concurrently (product decision 2026-07-01). The backend enforces only the
    // one-active-reservation-per-user rule, so we let the reservation proceed regardless of
    // whether the unit already shows "reserved" for someone else.

    this.reserveFlowSubmitting = true;
    this.reserveFlowError = null;
    this.reserveFlowResult = null;
    let leadId: string | null = null;
    try {
      const phoneE164 = this.toE164(this.reserveFlowClientPhone);
      const lead = await firstValueFrom(this.leadsService.createManualLead({
        propertyId: this.property?.id || '',
        fullName: this.reserveFlowClientName?.trim() || undefined,
        email: this.reserveFlowClientEmail?.trim() || undefined,
        phone: phoneE164 || undefined,
        status: 'new',
        verifiedChannel: this.verifiedChannel,
        verifiedTarget: this.verifiedChannel === 'email' ? this.verifiedEmailTarget : this.verifiedPhoneTarget,
        verifiedEmail: this.verifiedEmailTarget,
        verifiedPhone: this.verifiedPhoneTarget,
        fastReservationSessionId: this.reservationSessionId || null,
        paymentPlanId: this.reserveFlowPlanId || null
      }));
      leadId = String(lead.leadId || '').trim() || null;
      if (!leadId) throw new Error('Lead creation did not return a lead id');

      // Carry the verified fast-reservation session forward. createManualLead links
      // (or creates) a session and returns its id; attaching it to createReservation
      // is what tells the backend the client's identity was vouched for, so it skips
      // the KYC-eligibility gate and actually creates the reservation. Without this,
      // a missing session id made createReservation 403 with
      // RESERVATION_ELIGIBILITY_INCOMPLETE and the flow dead-ended at "lead created".
      const leadSessionId = String(lead.fastReservationSessionId || '').trim();
      if (leadSessionId) this.reservationSessionId = leadSessionId;

      // Refresh the leads list immediately after lead creation so the new
      // investor appears in the unit's leads even if the reservation step below fails.
      this.loadReservations(this.property?.id || '');

      const planId = this.reserveFlowPlanId || null;
      const reservation = await firstValueFrom(this.reservationsService.createReservation({
        property_id: this.property?.id || '',
        payment_plan_id: planId,
        fast_reservation_session_id: this.reservationSessionId || undefined
      }));
      this.reserveFlowResult = reservation;
      this.reserveFlowStep = 3;
      await this.loadKyc(reservation.reservationId || leadId);
      this.loadReservations(this.property?.id || '');
      this.notifications.showSuccess('Reservation created successfully.').subscribe();
    } catch (err: any) {
      const code = err?.error?.error?.code || err?.error?.code || null;
      if (code === 'RESERVATION_ELIGIBILITY_INCOMPLETE' && leadId) {
        // Identity (KYC) isn't complete yet, so the backend blocked the reservation.
        // The lead row IS the reservation row (same id, same table), so bind it as the
        // current result and open step 3 KYC against it — this lets the developer
        // complete identity and continue signing on the client's behalf, matching the
        // public property detail flow. Previously reserveFlowResult stayed null, so
        // reserveFlowReservationId was null and KYC/signing silently no-opped (the
        // reported "added as a lead but can't continue" dead-end).
        this.reserveFlowResult = { reservationId: leadId, leadId, paymentPlanId: this.reserveFlowPlanId || null };
        this.reserveFlowStep = 3;
        this.reserveFlowError = null;
        await this.loadKyc(leadId);
        this.notifications.showInfo('Complete the client\'s identity details below to continue the reservation.').subscribe();
        return;
      }
      if ((code === 'OFFER_APPENDIX_REQUIRED' || code === 'PAYMENT_PLAN_INCONSISTENT') && leadId) {
        // The reservation row was created, but the signing document can't be built
        // because this unit's offer PDF isn't available (typically an inconsistent
        // payment plan or unmet minimum offer requirements). Bind the reservation
        // (lead row IS the reservation row) and advance to step 3 so KYC can proceed
        // and the developer isn't dead-ended; surface a fix-offer-requirements action.
        // Continuing the SAME reservation also avoids PROPERTY_ALREADY_RESERVED on retry.
        const details = err?.error?.error?.details || err?.error?.details || null;
        const reason =
          details?.reason_message ||
          err?.error?.error?.message ||
          err?.error?.message ||
          err?.message ||
          null;
        this.reserveFlowResult = { reservationId: leadId, leadId, paymentPlanId: this.reserveFlowPlanId || null };
        this.reserveFlowStep = 3;
        this.reserveFlowOfferRequirementMissing = true;
        this.reserveFlowOfferRequirementReason = reason ? String(reason) : null;
        this.reserveFlowError = null;
        await this.loadKyc(leadId);
        this.notifications.showInfo('Reservation started. Fix this unit\'s offer requirements to enable the signing document.').subscribe();
        return;
      }
	      if (code === 'NOT_FOUND' || code === 'FAST_RESERVATION_SESSION_NOT_FOUND') {
	        this.reserveFlowError = 'This unit or its selected payment plan is no longer available. Refresh the unit and choose it again.';
	        return;
	      }
	      if (code === 'RESERVATION_FAILED') {
	        this.reserveFlowError = 'We could not complete this reservation right now. Please try again in a moment.';
	        return;
	      }
	      this.reserveFlowError = this.apiError(err, 'Failed to create reservation');
    } finally {
      this.reserveFlowSubmitting = false;
    }
  }

  // ─── Reserve On Behalf ────────────────────────────────────────────────────

  closeReserveOnBehalf(): void {
    this.reserveOnBehalfOpen = false;
    this.reserveOnBehalfStep = 'form';
    this.reserveOnBehalfError = null;
    this.reserveOnBehalfContinuing = false;
    this.reserveOnBehalfForm = { fullName: '', email: '', phone: '', countryCode: 'EG' };
    this.reserveOnBehalfOtpCode = '';
    this.reserveOnBehalfOtpTarget = '';
    this.reserveOnBehalfOtpTargetMasked = '';
    this.reserveOnBehalfSessionId = '';
    this.reserveOnBehalfResult = null;
    this.resetKycState();
  }

  countNameTokens(value: string | null | undefined): number {
    return String(value || '').trim().split(/\s+/).filter(Boolean).length;
  }

  get reserveOnBehalfFullNameValid(): boolean {
    return this.countNameTokens(this.reserveOnBehalfForm.fullName) >= 4;
  }

  get kycFullNameValid(): boolean {
    return this.countNameTokens(this.kycDraft['full_name']) >= 4;
  }

  get kycArabicFullNameValid(): boolean {
    return this.countNameTokens(this.kycDraft['arabic_full_name']) >= 4;
  }

  get kycDraftReadyForApproval(): boolean {
    const fn = String(this.kycDraft['full_name'] || '').trim();
    const idn = String(this.kycDraft['id_number'] || '').trim();
    const nat = String(this.kycDraft['nationality'] || '').trim();
    const bd = String(this.kycDraft['birth_date'] || '').trim();
    const hasBack = !this.kyc?.documentRequiresBack || Boolean(this.kyc?.idDocumentBackFileId);
    return Boolean(fn && idn && nat && bd && hasBack);
  }

  async submitReserveOnBehalfForm(): Promise<void> {
    if (!this.reserveOnBehalfFullNameValid) {
      this.reserveOnBehalfError = 'Full name must contain at least 4 names (e.g. Ahmed Ibrahim Mohamed Ahmed).';
      return;
    }
    this.reserveOnBehalfSubmitting = true;
    this.reserveOnBehalfError = null;
    try {
      const res = await firstValueFrom(this.reservationsService.reserveOnBehalf({
        full_name: this.reserveOnBehalfForm.fullName?.trim() || '',
        email: this.reserveOnBehalfForm.email?.trim() || null,
        phone: this.reserveOnBehalfForm.phone?.trim() || '',
        property_id: this.property?.id || '',
        payment_plan_id: this.offerDefaultsPlanId || null
      }));
      this.reserveOnBehalfSessionId = res.fastReservationSessionId || '';
      this.reserveOnBehalfOtpTarget = res.target || '';
      this.reserveOnBehalfOtpTargetMasked = res.targetMasked || '';
      this.reserveOnBehalfStep = 'otp';
    } catch (e: any) {
      this.reserveOnBehalfError = e?.error?.error?.message || e?.error?.message || e?.message || 'Error';
    } finally {
      this.reserveOnBehalfSubmitting = false;
    }
  }

  async verifyReserveOnBehalfOtp(): Promise<void> {
    this.reserveOnBehalfVerifying = true;
    this.reserveOnBehalfError = null;
    try {
      const data = await firstValueFrom(this.reservationSessionService.verifyOtp({
        channel: 'phone',
        code: this.reserveOnBehalfOtpCode,
        sessionId: this.reserveOnBehalfSessionId || null
      }));
      this.reservationSessionId = String(data.fast_reservation_session_id || this.reserveOnBehalfSessionId || '').trim();
      this.verifiedChannel = data.channel;
      if (data.channel === 'email') this.verifiedEmailTarget = data.target;
      else this.verifiedPhoneTarget = data.target;
      // Actually create the reservation now that OTP is verified
      const reservation = await firstValueFrom(this.reservationsService.createReservation({
        property_id: this.property?.id || '',
        payment_plan_id: this.offerDefaultsPlanId || null,
        fast_reservation_session_id: this.reservationSessionId || undefined
      }));
      this.reserveOnBehalfResult = reservation;
      this.reserveOnBehalfStep = 'success';
      const reservationId = this.reserveOnBehalfResult?.reservationId;
      if (reservationId) await this.loadKyc(reservationId);
      this.loadReservations(this.property?.id || '');
      this.notifications.showSuccess('Reservation created successfully.').subscribe();
    } catch (e: any) {
      this.reserveOnBehalfError = e?.error?.error?.message || e?.error?.message || e?.message || 'Failed to create reservation';
    } finally {
      this.reserveOnBehalfVerifying = false;
    }
  }

  // ─── KYC ──────────────────────────────────────────────────────────────────

  private resetKycState(): void {
    this.kycLoading = false;
    this.kycError = null;
    this.kyc = null;
    this.kycIdUploading = false;
    this.kycIdBackUploading = false;
    this.kycSaving = false;
    this.kycApproving = false;
    this.signing = false;
    this.rechecking = false;
    this.reserveFlowKycMode = 'choose';
    this.reserveFlowKycDocumentKindLabel = '';
    this.revokeKycPreview('front');
    this.revokeKycPreview('back');
    this.kycIdFrontFileName = '';
    this.kycIdBackFileName = '';
    this.kycDraft = {
      full_name: '', arabic_full_name: '', id_number: '', nationality: '',
      birth_date: '', address_line1: '', address_line2: '', address_city: '', address_country: ''
    };
  }

  private async loadKyc(reservationId: string): Promise<void> {
    const id = String(reservationId || '').trim();
    if (!id) return;
    this.resetKycState();
    this.kycLoading = true;
    try {
      this.kyc = await firstValueFrom(this.reservationsService.getReservationKyc(id));
      this.syncKycDraftFromRecord();
    } catch (err: any) {
      this.kycError = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to load identity details';
    } finally {
      this.kycLoading = false;
    }
  }

  private syncKycDraftFromRecord(): void {
    this.kycDraft = {
      full_name: this.kycFieldValue('full_name'),
      arabic_full_name: this.kycFieldValue('arabic_full_name'),
      id_number: this.kycFieldValue('id_number'),
      nationality: this.kycFieldValue('nationality'),
      birth_date: this.kycFieldValue('birth_date'),
      address_line1: this.kycFieldValue('address_line1'),
      address_line2: this.kycFieldValue('address_line2'),
      address_city: this.kycFieldValue('address_city'),
      address_country: this.kycFieldValue('address_country')
    };
    this.kycDraftSnapshot = { ...this.kycDraft };
    if (this.kyc?.documentKind) {
      this.reserveFlowKycDocumentKindLabel = this.kyc.documentKind === 'id_card' ? 'ID Card detected' : this.kyc.documentKind === 'passport' ? 'Passport detected' : '';
    }
    this.hydrateKycDocumentPreviews();
  }

  private hydrateKycDocumentPreviews(): void {
    const frontFileId = this.kyc?.idDocumentFileId ? String(this.kyc.idDocumentFileId).trim() : '';
    const backFileId = this.kyc?.idDocumentBackFileId ? String(this.kyc.idDocumentBackFileId).trim() : '';
    this.kycIdFrontFileName = frontFileId ? 'Front document attached' : '';
    this.kycIdBackFileName = backFileId ? 'Back document attached' : '';
    if (frontFileId) this.loadKycPreview(frontFileId, 'front');
    else this.revokeKycPreview('front');
    if (backFileId) this.loadKycPreview(backFileId, 'back');
    else this.revokeKycPreview('back');
  }

  private loadKycPreview(fileId: string, side: 'front' | 'back'): void {
    const id = String(fileId || '').trim();
    if (!id) return;
    this.revokeKycPreview(side);
    this.filesService.downloadPrivateFile(id).pipe(take(1)).subscribe({
      next: (blob) => {
        const objectUrl = URL.createObjectURL(blob);
        if (side === 'front') {
          this.kycIdFrontPreviewUrl = objectUrl;
          this.kycIdFrontPreviewIsImage = String(blob?.type || '').toLowerCase().startsWith('image/');
        } else {
          this.kycIdBackPreviewUrl = objectUrl;
          this.kycIdBackPreviewIsImage = String(blob?.type || '').toLowerCase().startsWith('image/');
        }
      },
      error: () => {
        if (side === 'front') { this.kycIdFrontPreviewUrl = null; this.kycIdFrontPreviewIsImage = false; }
        else { this.kycIdBackPreviewUrl = null; this.kycIdBackPreviewIsImage = false; }
      }
    });
  }

  private revokeKycPreview(side: 'front' | 'back'): void {
    if (side === 'front') {
      const url = this.kycIdFrontPreviewUrl;
      if (url && url.startsWith('blob:')) { try { URL.revokeObjectURL(url); } catch { /* ignore */ } }
      this.kycIdFrontPreviewUrl = null;
      this.kycIdFrontPreviewIsImage = false;
    } else {
      const url = this.kycIdBackPreviewUrl;
      if (url && url.startsWith('blob:')) { try { URL.revokeObjectURL(url); } catch { /* ignore */ } }
      this.kycIdBackPreviewUrl = null;
      this.kycIdBackPreviewIsImage = false;
    }
  }

  private kycFieldValue(key: 'full_name' | 'arabic_full_name' | 'id_number' | 'nationality' | 'birth_date' | 'address_line1' | 'address_line2' | 'address_city' | 'address_country'): string {
    const data = this.kyc?.confirmedData && typeof this.kyc.confirmedData === 'object' ? this.kyc.confirmedData : {};
    const extracted = this.kyc?.extractedNormalized && typeof this.kyc.extractedNormalized === 'object' ? this.kyc.extractedNormalized : {};
    const address = (data as any).address && typeof (data as any).address === 'object' ? (data as any).address : {};
    const candidates: any[] = [];
    if (key === 'full_name') candidates.push((data as any).full_name, (data as any).FULL_NAME, (data as any).fullName, (data as any).name, (extracted as any).full_name, (extracted as any).fullName);
    if (key === 'arabic_full_name') candidates.push((data as any).arabic_full_name, (data as any).ARABIC_FULL_NAME, (data as any).arabicFullName, (extracted as any).arabic_full_name, (extracted as any).arabicFullName);
    if (key === 'id_number') candidates.push((data as any).id_number, (data as any).ID_NUMBER, (data as any).idNumber, (extracted as any).id_number, (extracted as any).idNumber);
    if (key === 'nationality') candidates.push((data as any).nationality, (data as any).NATIONALITY, (extracted as any).nationality);
    if (key === 'birth_date') candidates.push((data as any).birth_date, (data as any).BIRTH_DATE, (data as any).date_of_birth, (extracted as any).birth_date, (extracted as any).date_of_birth, (extracted as any).dateOfBirth);
    if (key === 'address_line1') candidates.push(address.line1, (data as any).address_line1, (extracted as any).address_line1, (extracted as any).addressLine1);
    if (key === 'address_line2') candidates.push(address.line2, (data as any).address_line2, (extracted as any).address_line2, (extracted as any).addressLine2);
    if (key === 'address_city') candidates.push(address.city, (data as any).address_city, (extracted as any).address_city, (extracted as any).addressCity);
    if (key === 'address_country') candidates.push(address.country, (data as any).address_country, (extracted as any).address_country, (extracted as any).addressCountry);
    const value = candidates.find(v => v !== null && v !== undefined && String(v).trim());
    return value !== null && value !== undefined && String(value).trim() ? String(value).trim() : '';
  }

  private kycDraftDirty(): boolean {
    const draft = this.kycDraft as Record<string, string>;
    const snapshot = this.kycDraftSnapshot || {};
    for (const k of Object.keys(draft)) {
      if (String(draft[k] || '').trim() !== String(snapshot[k] || '').trim()) return true;
    }
    return false;
  }

  async saveKyc(): Promise<boolean> {
    const reservationId = this.reserveFlowReservationId;
    if (!reservationId || this.kycSaving) return false;
    if (!this.kycDraftDirty()) return true;
    const fullNameVal = String(this.kycDraft['full_name'] || '').trim();
    const arabicFullNameVal = String(this.kycDraft['arabic_full_name'] || '').trim();
    if (fullNameVal && this.countNameTokens(fullNameVal) < 4) {
      this.kycError = 'English full name must contain at least 4 names (e.g. Ahmed Ibrahim Mohamed Ahmed).';
      this.notifications.showError(this.kycError).subscribe();
      return false;
    }
    if (arabicFullNameVal && this.countNameTokens(arabicFullNameVal) < 4) {
      this.kycError = 'Arabic full name must contain at least 4 names.';
      this.notifications.showError(this.kycError).subscribe();
      return false;
    }
    const trimOrNull = (v: string) => { const s = String(v || '').trim(); return s || null; };
    const confirmed: any = {};
  const fullName = trimOrNull(this.kycDraft['full_name']);
  const arabicFullName = trimOrNull(this.kycDraft['arabic_full_name']);
  const idNumber = trimOrNull(this.kycDraft['id_number']);
  const nationality = trimOrNull(this.kycDraft['nationality']);
  const birthDate = trimOrNull(this.kycDraft['birth_date']);
  const addressLine1 = trimOrNull(this.kycDraft['address_line1']);
  const addressLine2 = trimOrNull(this.kycDraft['address_line2']);
  const addressCity = trimOrNull(this.kycDraft['address_city']);
  const addressCountry = trimOrNull(this.kycDraft['address_country']);
    if (fullName) confirmed.full_name = fullName;
    if (arabicFullName) confirmed.arabic_full_name = arabicFullName;
    if (idNumber) confirmed.id_number = idNumber;
    if (nationality) confirmed.nationality = nationality;
    if (birthDate) confirmed.birth_date = birthDate;
    if (addressLine1 || addressLine2 || addressCity || addressCountry) {
      confirmed.address = {
        ...(addressLine1 ? { line1: addressLine1 } : {}),
        ...(addressLine2 ? { line2: addressLine2 } : {}),
        ...(addressCity ? { city: addressCity } : {}),
        ...(addressCountry ? { country: addressCountry } : {})
      };
    }
    this.kycError = null;
    this.kycSaving = true;
    try {
      this.kyc = await firstValueFrom(this.reservationsService.updateReservationKyc(reservationId, confirmed));
      this.syncKycDraftFromRecord();
      this.notifications.showSuccess('KYC details saved').subscribe();
      // Clear the cached PDF file id on the active result so the next
      // "Open PDF" / "Prepare signing" click regenerates with the updated data.
      if (this.reserveFlowResult) {
        this.reserveFlowResult = { ...this.reserveFlowResult, pdfFileId: null };
      } else if (this.reserveOnBehalfResult) {
        this.reserveOnBehalfResult = { ...this.reserveOnBehalfResult, pdfFileId: null };
      }
      // The backend detached the stale reservation PDF on save, so trigger
      // regeneration in the background to refresh the cached pdfFileId.
      this.regenerateReservationPdfAfterKyc(reservationId);
      return true;
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to save KYC details';
      this.kycError = msg;
      this.notifications.showError(msg).subscribe();
      return false;
    } finally {
      this.kycSaving = false;
    }
  }

  async approveKyc(): Promise<void> {
    const reservationId = this.reserveFlowReservationId;
    if (!reservationId || this.kycApproving) return;
    this.kycError = null;
    const saved = await this.saveKyc();
    if (!saved) return;
    this.kycApproving = true;
    try {
      this.kyc = await firstValueFrom(this.reservationsService.approveReservationKyc(reservationId));
      this.syncKycDraftFromRecord();
      this.notifications.showSuccess('Investor verified').subscribe();
      // The backend detached the stale reservation PDF on approve, so the next
      // signing/PDF call regenerates it with the now-verified identity data.
      // Trigger that regeneration in the background and refresh the cached
      // pdfFileId/zohoSign on the active result + lead list row.
      this.regenerateReservationPdfAfterKyc(reservationId);
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to approve identity';
      const missing = err?.error?.error?.details?.missing;
      const msgWithMissing = Array.isArray(missing) && missing.length ? `${msg}: ${this.missingFieldsLabel(missing)}` : msg;
      this.kycError = msgWithMissing;
      this.notifications.showError(msgWithMissing).subscribe();
    } finally {
      this.kycApproving = false;
    }
  }

  private async regenerateReservationPdfAfterKyc(reservationId: string): Promise<void> {
    try {
      const result = await firstValueFrom(this.reservationsService.ensureZohoSign(reservationId, {
        force: false,
        paymentPlanId: this.reserveFlowPlanId || this.offerDefaultsPlanId || null
      }));
      const pdfFileId = result?.pdfFileId ?? null;
      const zohoSign = result?.zohoSign ?? null;
      if (this.reserveFlowResult) {
        this.reserveFlowResult = {
          ...this.reserveFlowResult,
          pdfFileId: pdfFileId ?? this.reserveFlowResult.pdfFileId ?? null,
          zohoSign: zohoSign ?? this.reserveFlowResult.zohoSign ?? null
        };
      } else if (this.reserveOnBehalfResult) {
        this.reserveOnBehalfResult = {
          ...this.reserveOnBehalfResult,
          pdfFileId: pdfFileId ?? this.reserveOnBehalfResult.pdfFileId ?? null,
          zohoSign: zohoSign ?? this.reserveOnBehalfResult.zohoSign ?? null
        };
      }
      const idx = this.reservations.findIndex(x => x.id === reservationId);
      if (idx >= 0) {
        this.reservations[idx] = {
          ...this.reservations[idx],
          pdfFileId: pdfFileId ?? this.reservations[idx].pdfFileId ?? null,
          zohoSign: zohoSign ?? this.reservations[idx].zohoSign ?? null
        };
      }
      if (pdfFileId) {
        this.notifications.showInfo('Reservation PDF updated with the latest details.').subscribe();
      }
    } catch (err: any) {
      // Non-fatal: the cache was cleared server-side, so the next "Open PDF"
      // click will regenerate. Surface a soft warning so the developer knows
      // the PDF wasn't refreshed immediately.
      console.warn('[approveKyc] PDF regeneration deferred:', err?.message || err);
      const regenMsg = err?.error?.error?.message || err?.error?.message || err?.message || 'Reservation PDF will be regenerated on next open.';
      this.notifications.showWarning(regenMsg).subscribe();
    }
  }

  // ─── KYC Document Upload / Camera ─────────────────────────────────────────

  openReserveFlowKycDocument(side: 'front' | 'back'): void {
    const reservationId = this.reserveFlowReservationId;
    if (!reservationId) return;
    const fileId = side === 'front' ? this.kyc?.idDocumentFileId : this.kyc?.idDocumentBackFileId;
    if (fileId) {
      this.viewerDialog.openPrivateFile(fileId, side === 'front' ? 'ID Document' : 'ID Document (Back)');
    }
  }

  openKycCamera(side: 'front' | 'back'): void {
    this.kycCameraVisible = true;
    this.kycCameraSide = side;
    this.kycCameraCaptureUrl = null;
    this.kycCameraStarting = false;
    this.kycCameraError = null;
    this.startKycCameraStream();
  }

  closeKycCamera(): void {
    this.kycCameraVisible = false;
    this.kycCameraCaptureUrl = null;
    this.kycCameraStarting = false;
    this.kycCameraError = null;
    this.stopKycCameraStream();
  }

  private startKycCameraStream(): void {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.kycCameraError = 'Camera is not available on this device.';
      return;
    }
    this.kycCameraStarting = true;
    this.kycCameraError = null;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } }).then(stream => {
      this.kycCameraStream = stream;
      if (this.kycCameraVideo?.nativeElement) {
        this.kycCameraVideo.nativeElement.srcObject = stream;
        this.kycCameraVideo.nativeElement.play().catch(() => {});
      }
      this.kycCameraStarting = false;
    }).catch(err => {
      this.kycCameraError = err?.message || 'Failed to start camera';
      this.kycCameraStarting = false;
    });
  }

  private stopKycCameraStream(): void {
    if (this.kycCameraStream) {
      this.kycCameraStream.getTracks().forEach(t => t.stop());
      this.kycCameraStream = null;
    }
  }

  captureKycPhoto(): void {
    const video = this.kycCameraVideo?.nativeElement;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    this.kycCameraCaptureUrl = canvas.toDataURL('image/jpeg', 0.85);
    this.stopKycCameraStream();
  }

  retakeKycPhoto(): void {
    this.kycCameraCaptureUrl = null;
    this.startKycCameraStream();
  }

  async confirmKycPhoto(): Promise<void> {
    if (!this.kycCameraCaptureUrl) return;
    const reservationId = this.reserveFlowReservationId;
    if (!reservationId) return;
    const isBack = this.kycCameraSide === 'back';
    if (isBack) this.kycIdBackUploading = true; else this.kycIdUploading = true;
    this.kycError = null;
    try {
      const blob = await (await fetch(this.kycCameraCaptureUrl)).blob();
      const file = new File([blob], `id-${this.kycCameraSide}.jpg`, { type: 'image/jpeg' });
      const upload = await firstValueFrom(this.uploads.uploadFile(file, { owner: 'org' }));
      if (isBack) {
        this.kyc = await firstValueFrom(this.reservationsService.setReservationKycIdDocumentBack(reservationId, upload.file_id));
      } else {
        this.kyc = await firstValueFrom(this.reservationsService.setReservationKycIdDocument(reservationId, upload.file_id));
        if (this.reserveFlowKycMode === 'ai' && this.kyc?.documentKind) {
          await firstValueFrom(this.reservationsService.setReservationKycIdDocumentType(reservationId, upload.file_id));
          this.kyc = await firstValueFrom(this.reservationsService.getReservationKyc(reservationId));
        }
      }
      this.syncKycDraftFromRecord();
      this.closeKycCamera();
      this.notifications.showSuccess(`ID ${isBack ? 'back' : 'front'} uploaded`).subscribe();
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || err?.message || `Failed to upload ID ${isBack ? 'back' : 'front'}`;
      this.kycError = msg;
      this.notifications.showError(msg).subscribe();
    } finally {
      this.kycIdUploading = false;
      this.kycIdBackUploading = false;
    }
  }

  /** Upload an ID document (front/back) from the user's device (file picker), beside the camera capture flow. */
  async onKycIdFileSelected(side: 'front' | 'back', event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0] || null;
    if (target) target.value = '';
    if (!file) return;
    const isBack = side === 'back';
    if (isBack ? this.kycIdBackUploading : this.kycIdUploading) return;
    const reservationId = this.reserveFlowReservationId;
    if (!reservationId) {
      this.notifications.showError('Start the reservation first to upload an ID document.').subscribe();
      return;
    }
    if (isBack) this.kycIdBackUploading = true; else this.kycIdUploading = true;
    this.kycError = null;
    try {
      const upload = await firstValueFrom(this.uploads.uploadFile(file, { owner: 'org' }));
      if (isBack) {
        this.kyc = await firstValueFrom(this.reservationsService.setReservationKycIdDocumentBack(reservationId, upload.file_id));
      } else {
        this.kyc = await firstValueFrom(this.reservationsService.setReservationKycIdDocument(reservationId, upload.file_id));
        if (this.reserveFlowKycMode === 'ai' && this.kyc?.documentKind) {
          await firstValueFrom(this.reservationsService.setReservationKycIdDocumentType(reservationId, upload.file_id));
          this.kyc = await firstValueFrom(this.reservationsService.getReservationKyc(reservationId));
        }
      }
      this.syncKycDraftFromRecord();
      this.notifications.showSuccess(`ID ${isBack ? 'back' : 'front'} uploaded`).subscribe();
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || err?.message || `Failed to upload ID ${isBack ? 'back' : 'front'}`;
      this.kycError = msg;
      this.notifications.showError(msg).subscribe();
    } finally {
      this.kycIdUploading = false;
      this.kycIdBackUploading = false;
    }
  }

  // ─── Signing / PDF ────────────────────────────────────────────────────────

  async openReservationPdf(): Promise<void> {
    const reservationId = this.reserveFlowReservationId;
    if (reservationId) {
      const saved = await this.saveKyc();
      if (!saved) return;
    }
    const fileId = this.reserveFlowResult?.pdfFileId || this.reserveOnBehalfResult?.pdfFileId || null;
    if (fileId && reservationId) {
      this.viewerDialog.open({ privateFileId: fileId, title: 'Reservation PDF', reservationId });
      return;
    }
    if (fileId) {
      this.viewerDialog.openPrivateFile(fileId, 'Reservation PDF');
      return;
    }
    if (reservationId) {
      this.startPdfPreparing(reservationId, 'Reservation PDF');
      return;
    }
    this.notifications.showInfo('Reservation PDF is not available yet.').subscribe();
  }

  async generateSigningLink(): Promise<void> {
    const reservationId = this.reserveFlowReservationId;
    if (!reservationId || this.signing) return;
    if (!this.zohoSignEnabled) {
      await this.openReservationPdf();
      return;
    }
    this.kycError = null;
    const saved = await this.saveKyc();
    if (!saved) return;
    this.signing = true;
    try {
      const result = await firstValueFrom(this.reservationsService.ensureZohoSign(reservationId, {
        force: true,
        paymentPlanId: this.reserveFlowPlanId || this.offerDefaultsPlanId || null
      }));
      if (result?.zohoSign?.sign_url) {
        // Update whichever result is active (reserve flow or on-behalf)
        const active = this.activeZohoSignResult;
        const updated = {
          ...(active || {}),
          pdfFileId: result.pdfFileId ?? active?.pdfFileId ?? null,
          zohoSign: result.zohoSign
        };
        if (this.reserveFlowResult) this.reserveFlowResult = updated;
        else if (this.reserveOnBehalfResult) this.reserveOnBehalfResult = updated;
        this.openReserveFlowSigningViewer();
      } else {
        this.notifications.showWarning('Signing link is still preparing. Please try again in a moment.').subscribe();
      }
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to generate signing link';
      this.kycError = msg;
      this.notifications.showError(msg).subscribe();
    } finally {
      this.signing = false;
    }
  }

  openReserveFlowSigningViewer(): void {
    const reservationId = this.reserveFlowReservationId;
    if (!reservationId) return;
    this.viewerDialog.open({
      title: 'Signing',
      reservationId,
      paymentPlanId: this.reserveFlowPlanId || this.offerDefaultsPlanId || undefined
    });
  }

  async recheckReservationFlow(): Promise<void> {
    const reservationId = this.reserveFlowReservationId;
    if (!reservationId || this.rechecking) return;
    this.rechecking = true;
    this.kycError = null;
    try {
      const zohoResult = await firstValueFrom(this.reservationsService.getZohoSignStatus(reservationId, { refresh: true }));
      if (this.reserveFlowResult) {
        this.reserveFlowResult = {
          ...this.reserveFlowResult,
          pdfFileId: zohoResult.pdfFileId ?? this.reserveFlowResult.pdfFileId ?? null,
          zohoSign: zohoResult.zohoSign ?? this.reserveFlowResult.zohoSign ?? null
        };
      }
      await this.loadKyc(reservationId);
      this.notifications.showSuccess('Reservation flow refreshed.').subscribe();
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to re-check reservation flow';
      this.kycError = msg;
      this.notifications.showError(msg).subscribe();
    } finally {
      this.rechecking = false;
    }
  }

  // ─── Lead Section: Signing / PDF / Recheck ──────────────────────────────────────

  /** Unified zoho sign state from either reserve flow or on-behalf result */
  get activeZohoSignResult(): ReservationResult | null {
    return this.reserveFlowResult || this.reserveOnBehalfResult || null;
  }

  openLeadReservationPdf(r: ReservationRecord): void {
    if (r.pdfFileId && r.id) {
      this.viewerDialog.open({ privateFileId: r.pdfFileId, title: 'Reservation PDF', reservationId: r.id });
      return;
    }
    if (r.pdfFileId) {
      this.viewerDialog.openPrivateFile(r.pdfFileId, 'Reservation PDF');
      return;
    }
    if (r.id) {
      this.startPdfPreparing(r.id, 'Reservation PDF');
      return;
    }
    this.notifications.showInfo('Reservation PDF is not available yet.').subscribe();
  }

  // ─── Reservation PDF preparing popup ──────────────────────────────────────
  // When the reservation PDF hasn't been generated yet (e.g. a "qualified" lead
  // with no contract row), "Open PDF" opens a popup that calls the backend to
  // generate/fetch the PDF (POST ensureZohoSign), then polls the status until
  // the PDF is ready and opens the viewer automatically.

  private startPdfPreparing(reservationId: string, title = 'Reservation PDF'): void {
    this.cancelPdfPreparing();
    this.pdfPreparing = true;
    this.pdfPreparingReservationId = reservationId;
    this.pdfPreparingTitle = title;
    this.pdfPreparingCountdown = 0;
    this.pdfPreparingAttempts = 0;
    this.pdfPreparingError = null;
    this.pdfPreparingErrorCode = null;
    this.pdfPreparingPolling = false;
    this.pdfPreparingStopped = false;
    this.pollPdfOnce();
    this.startPdfCountdown();
  }

  private startPdfCountdown(): void {
    this.stopPdfCountdown();
    this.pdfPreparingTimer = setInterval(() => {
      if (this.pdfPreparingStopped || this.pdfPreparingPolling) return;
      this.pdfPreparingCountdown -= 1;
      if (this.pdfPreparingCountdown <= 0) {
        this.pollPdfOnce();
      }
    }, 1000);
  }

  private stopPdfCountdown(): void {
    if (this.pdfPreparingTimer) {
      clearInterval(this.pdfPreparingTimer);
      this.pdfPreparingTimer = null;
    }
  }

  private async pollPdfOnce(): Promise<void> {
    if (this.pdfPreparingPolling || !this.pdfPreparing || !this.pdfPreparingReservationId) return;
    const reservationId = this.pdfPreparingReservationId;
    this.pdfPreparingPolling = true;
    this.pdfPreparingCountdown = 0;
    this.pdfPreparingError = null;
    try {
      // First attempt: ask the backend to GENERATE the PDF (POST ensureZohoSign).
      // Subsequent attempts: just refresh the status (GET refresh) to avoid
      // repeatedly creating Zoho Sign requests.
      const isFirstAttempt = this.pdfPreparingAttempts === 0;
      let pdfFileId: string | null = null;
      let zohoSign: ReservationResult['zohoSign'] = null;
      if (isFirstAttempt) {
        const result = await firstValueFrom(this.reservationsService.ensureZohoSign(reservationId, { force: false }));
        pdfFileId = result?.pdfFileId || null;
        zohoSign = result?.zohoSign ?? null;
      } else {
        const result = await firstValueFrom(this.reservationsService.getZohoSignStatus(reservationId, { refresh: true }));
        pdfFileId = result?.pdfFileId || null;
        zohoSign = result?.zohoSign ?? null;
      }
      // Update lead record if it's in the list.
      const idx = this.reservations.findIndex(x => x.id === reservationId);
      if (idx >= 0) {
        this.reservations[idx] = {
          ...this.reservations[idx],
          pdfFileId: pdfFileId ?? this.reservations[idx].pdfFileId ?? null,
          zohoSign: zohoSign ?? this.reservations[idx].zohoSign ?? null
        };
      }
      // Update reserve-flow / on-behalf result.
      if (this.reserveFlowResult?.reservationId === reservationId) {
        this.reserveFlowResult = {
          ...this.reserveFlowResult,
          pdfFileId: pdfFileId ?? this.reserveFlowResult.pdfFileId ?? null,
          zohoSign: zohoSign ?? this.reserveFlowResult.zohoSign ?? null
        };
      } else if (this.reserveOnBehalfResult?.reservationId === reservationId) {
        this.reserveOnBehalfResult = {
          ...this.reserveOnBehalfResult,
          pdfFileId: pdfFileId ?? this.reserveOnBehalfResult.pdfFileId ?? null,
          zohoSign: zohoSign ?? this.reserveOnBehalfResult.zohoSign ?? null
        };
      }
      if (pdfFileId) {
        const title = this.pdfPreparingTitle;
        const reservationId = this.pdfPreparingReservationId;
        this.cancelPdfPreparing();
        if (reservationId) {
          this.viewerDialog.open({ privateFileId: pdfFileId, title, reservationId });
        } else {
          this.viewerDialog.openPrivateFile(pdfFileId, title);
        }
        return;
      }
      this.pdfPreparingAttempts += 1;
      if (this.pdfPreparingAttempts >= this.pdfPreparingMaxAttempts) {
        this.pdfPreparingStopped = true;
        this.stopPdfCountdown();
        this.pdfPreparingError = 'Taking longer than usual. Click "Check now" to retry, or cancel and try again later.';
        return;
      }
      this.pdfPreparingCountdown = 5;
    } catch (err: any) {
      this.pdfPreparingStopped = true;
      this.stopPdfCountdown();
      const code = err?.error?.error?.code || err?.error?.code || err?.code || '';
      this.pdfPreparingErrorCode = code || null;
      if (code === 'PAYMENT_PLAN_REQUIRED') {
        this.pdfPreparingError = 'A payment plan is required before the reservation PDF can be generated. Use "Prepare signing" to pick a plan first.';
      } else if (code === 'RESERVATION_ELIGIBILITY_INCOMPLETE') {
        this.pdfPreparingError = err?.error?.error?.message || err?.error?.message || err?.message || 'Reservation eligibility is incomplete. Complete the KYC identity details and ID document upload first.';
      } else {
        this.pdfPreparingError = this.apiError(err, 'Failed to fetch reservation PDF');
      }
    } finally {
      this.pdfPreparingPolling = false;
    }
  }

  checkPdfNow(): void {
    if (!this.pdfPreparing || this.pdfPreparingPolling) return;
    this.pdfPreparingStopped = false;
    this.pdfPreparingError = null;
    if (!this.pdfPreparingTimer) {
      this.pdfPreparingCountdown = 5;
      this.startPdfCountdown();
    }
    this.pollPdfOnce();
  }

  cancelPdfPreparing(): void {
    this.stopPdfCountdown();
    this.pdfPreparing = false;
    this.pdfPreparingReservationId = null;
    this.pdfPreparingCountdown = 0;
    this.pdfPreparingAttempts = 0;
    this.pdfPreparingError = null;
    this.pdfPreparingErrorCode = null;
    this.pdfPreparingPolling = false;
    this.pdfPreparingStopped = false;
  }

  async continueKycFromPdfPreparing(): Promise<void> {
    const reservationId = this.pdfPreparingReservationId;
    if (!reservationId) return;
    this.cancelPdfPreparing();
    await this.openKycForReservation(reservationId);
  }

  async editLeadKyc(r: ReservationRecord): Promise<void> {
    if (!r.id) return;
    await this.openKycForReservation(r.id);
  }

  /**
   * Open the "Reserve for client" modal (reserveFlowOpen) pre-filled with a
   * lead's contact info. The developer reviews the prefilled fields, verifies
   * the OTP, and submits — the existing lead row (same table) flips from
   * "new" to "reserved" via the backend ON CONFLICT upsert in createManualLead
   * + createReservation. We do NOT auto-submit.
   */
  reserveForLead(r: ReservationRecord): void {
    if (this.reserveOnBehalfSubmitting || this.reserveOnBehalfVerifying) return;
    this.closeReserveFlow();
    const { countryCode, national } = this.splitCountryCodeFromPhone(r.phone || '');
    this.reserveFlowOpen = true;
    this.reserveFlowStep = 1;
    this.reserveFlowError = null;
    this.reserveFlowResult = null;
    this.reserveFlowOfferRequirementMissing = false;
    this.reserveFlowOfferRequirementReason = null;
    this.reserveFlowPlanId = this.offerDefaultsPlanId || (this.property?.paymentPlans?.[0]?.id ?? null);
    this.reserveFlowClientName = (r.customer && r.customer !== '—' && r.customer !== 'Unnamed lead' ? String(r.customer) : null);
    this.reserveFlowClientEmail = r.email || null;
    this.reserveFlowClientPhone = national ? { countryCode, phone: national } : null;
    this.reserveFlowOtpChannel = 'phone';
    this.reserveFlowOtpCode = '';
    this.reserveFlowSubmitting = false;
    this.reserveFlowContactValidating = false;
    this.reservationSessionId = '';
    this.otpError = null;
    this.otpSending = false;
    this.otpTargetMasked = null;
    this.otpVerifying = false;
    this.verifiedChannel = null;
    this.verifiedEmailTarget = null;
    this.verifiedPhoneTarget = null;
    this.investorSearchQuery = '';
    this.investorSearchResults = [];
    this.investorSearchDropdownOpen = false;
    this.selectedInvestorId = null;
    this.selectedInvestor = null;
    this.resetKycState();
    this.notifications.showInfo('Lead details pre-filled. Review and send OTP to reserve.').subscribe();
  }

  /**
   * Best-effort split of an E.164-ish phone (e.g. "+201234567890") into a
   * supported 2-letter countryCode recognized by the reserve-on-behalf form,
   * and the trailing national number. Falls back to EG + the raw number.
   */
  private splitCountryCodeFromPhone(raw: string): { countryCode: string; national: string } {
    const s = String(raw || '').trim();
    if (!s) return { countryCode: 'EG', national: '' };
    const PREFIX_TO_CODE: Array<[string, string]> = [
      ['20', 'EG'], ['966', 'SA'], ['971', 'AE'],
      ['1', 'US'], ['44', 'GB'], ['965', 'KW'],
      ['974', 'QA'], ['973', 'BH'], ['968', 'OM'], ['962', 'JO']
    ];
    const m = s.match(/^\+?(\d{1,3})\s*([\d\s-]+)/);
    if (m) {
      const cc = m[1];
      const rest = m[2].replace(/[\s-]/g, '');
      const match = PREFIX_TO_CODE.find(([prefix]) => prefix === cc);
      if (match) return { countryCode: match[1], national: rest };
    }
    return { countryCode: 'EG', national: s.replace(/^\+/, '') };
  }

  private async openKycForReservation(reservationId: string): Promise<void> {
    // Open the reserve-on-behalf modal directly at the KYC step for an
    // existing reservation that needs identity/document completion or editing.
    this.closeReserveFlow();
    this.reserveOnBehalfContinuing = true;
    this.reserveOnBehalfOpen = true;
    this.reserveOnBehalfStep = 'success';
    this.reserveOnBehalfError = null;
    this.reserveOnBehalfResult = { reservationId };
    this.resetKycState();
    this.reserveFlowKycMode = 'manual'; // skip chooser — show all details expanded
    await this.loadKyc(reservationId);
  }

  async generateLeadSigningLink(r: ReservationRecord): Promise<void> {
    if (!r.id || this.signing) return;
    if (!this.zohoSignEnabled) {
      this.openLeadReservationPdf(r);
      return;
    }
    this.signing = true;
    try {
      const result = await firstValueFrom(this.reservationsService.ensureZohoSign(r.id, { force: true }));
      if (result?.zohoSign?.sign_url) {
        this.viewerDialog.open({ title: 'Signing', reservationId: r.id });
      } else {
        this.notifications.showWarning('Signing link is still preparing. Please try again in a moment.').subscribe();
      }
      const idx = this.reservations.findIndex(x => x.id === r.id);
      if (idx >= 0) {
        this.reservations[idx] = {
          ...this.reservations[idx],
          pdfFileId: result.pdfFileId ?? this.reservations[idx].pdfFileId,
          zohoSign: result.zohoSign ?? this.reservations[idx].zohoSign
        };
      }
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to generate signing link';
      this.notifications.showError(msg).subscribe();
    } finally {
      this.signing = false;
    }
  }

  openLeadSigningViewer(r: ReservationRecord): void {
    if (!r.id) return;
    this.viewerDialog.open({ title: 'Signing', reservationId: r.id });
  }

  async recheckLeadReservation(r: ReservationRecord): Promise<void> {
    if (!r.id || this.rechecking) return;
    this.rechecking = true;
    try {
      const zohoResult = await firstValueFrom(this.reservationsService.getZohoSignStatus(r.id, { refresh: true }));
      const idx = this.reservations.findIndex(x => x.id === r.id);
      if (idx >= 0) {
        this.reservations[idx] = {
          ...this.reservations[idx],
          pdfFileId: zohoResult.pdfFileId ?? this.reservations[idx].pdfFileId,
          zohoSign: zohoResult.zohoSign ?? this.reservations[idx].zohoSign
        };
      }
      this.notifications.showSuccess('Reservation refreshed.').subscribe();
    } catch (err: any) {
      const msg = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to re-check';
      this.notifications.showError(msg).subscribe();
    } finally {
      this.rechecking = false;
    }
  }

  // ─── Lead Plan Modal ──────────────────────────────────────────────────────

  leadPlanModalTitle(): string { return this.leadPlanMode === 'create' ? 'Create Lead' : 'Assign Plan'; }
  leadPlanModalDescription(): string { return this.leadPlanMode === 'create' ? 'Create a new lead for this unit' : 'Select a payment plan for this lead'; }

  closeLeadPlanModal(): void { this.leadPlanModalOpen = false; }

  async confirmLeadPlanModal(): Promise<void> {
    if (!this.leadPlanSelectedId || this.creatingLead || this.sendingOffers) return;
    this.creatingLead = true;
    this.leadPlanError = null;
    try {
      if (this.leadPlanMode === 'create') {
        const result = await firstValueFrom(this.offersService.requestOffer(this.property?.id || '', {
          payment_plan_id: this.leadPlanSelectedId
        }));
        this.notifications.showSuccess('Lead and offer created').subscribe();
      }
      this.leadPlanModalOpen = false;
      this.loadReservations(this.property?.id || '');
    } catch (err: any) {
      this.leadPlanError = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to create lead';
    } finally {
      this.creatingLead = false;
    }
  }

  // ─── Phone / E.164 Helpers ────────────────────────────────────────────────

  private toE164(value: any): string | null {
    if (!value) return null;
    const countryCode = String(value?.countryCode || '').toUpperCase().trim();
    const rawPhone = String(value?.phone || value || '');
    const digits = rawPhone.replace(/[^\d]/g, '').replace(/^0+/, '');
    if (!countryCode || !digits) return null;
    const dialMap: Record<string, string> = { EG: '+20', AE: '+971', SA: '+966', US: '+1', GB: '+44', DE: '+49', FR: '+33', KW: '+965', BH: '+973', QA: '+974', OM: '+968', JO: '+962', LB: '+961', IQ: '+964' };
    const dial = dialMap[countryCode] || '';
    if (!dial) return digits.length >= 8 ? `+${digits}` : null;
    return `${dial}${digits}`;
  }

  private fromE164(value: string | null | undefined): { countryCode: string; phone: string } | null {
    const raw = String(value || '').trim();
    if (!raw) return null;
    const dialMap: Record<string, string> = { '+20': 'EG', '+971': 'AE', '+966': 'SA', '+1': 'US', '+44': 'GB', '+49': 'DE', '+33': 'FR', '+965': 'KW', '+973': 'BH', '+974': 'QA', '+968': 'OM', '+962': 'JO', '+961': 'LB', '+964': 'IQ' };
    const match = Object.entries(dialMap).reduce((best, [dial, code]) => {
      if (raw.startsWith(dial) && dial.length > (best?.dial.length || 0)) return { dial, code };
      return best;
    }, null as { dial: string; code: string } | null);
    if (!match) return { countryCode: 'EG', phone: raw.replace(/[^\d]/g, '') };
    return { countryCode: match.code, phone: raw.slice(match.dial.length).replace(/^0+/, '') };
  }

  private currentVerificationTarget(channel: ReservationOtpChannel): string | null {
    if (channel === 'email') {
      const email = String(this.reserveFlowClientEmail || '').trim();
      return email && email.includes('@') ? email : null;
    }
    return this.toE164(this.reserveFlowClientPhone);
  }

  private clearVerificationState(channel?: ReservationOtpChannel): void {
    if (!channel || channel === 'email') this.verifiedEmailTarget = null;
    if (!channel || channel === 'phone') this.verifiedPhoneTarget = null;
    this.verifiedChannel = this.verifiedEmailTarget ? 'email' : this.verifiedPhoneTarget ? 'phone' : null;
    this.otpTargetMasked = null;
    this.otpError = null;
  }
}
